import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    NgZone,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { saveAs } from 'file-saver';
import { combineLatest, concat, from, merge, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, take, tap } from 'rxjs/operators';
import { Role } from 'src/app/classes/auth/role.class';
import { SelectExecutorDialogComponent } from 'src/app/select-executor-dialog/select-executor-dialog.component';
import { AfasService } from 'src/app/services/afas.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { PlanningDetailDialogService } from 'src/app/services/dialog/planning-detail-dialog.service';
import { EntitiesService } from 'src/app/services/entities/entities.service';
import { EntityId, EntityType } from 'src/app/services/entities/entity-type.class';
import { LeaveService } from 'src/app/services/leave.service';
import { FormattedAddress } from 'src/app/services/location.service';
import { PlanningHasService } from 'src/app/services/planning/planning-has.service';
import { PlanningService } from 'src/app/services/planning/planning.service';
import { UserPlanningService } from 'src/app/services/planning/user-planning.service';
import { ProjectService } from 'src/app/services/project.service';
import { PersonnelPlanningSettings, USER_SETTINGS_MODULE_KEYS } from 'src/app/services/user/user-settings';
import { UserService } from 'src/app/services/user/user.service';
import { Settings } from 'src/app/settings.class';
import { LocalStorage } from 'src/app/storage.class';
import { Utils } from 'src/app/utils.class';
import { environment } from '../../../environments/environment';
import { Entity } from '../../classes/entity.class';
import { Leave } from '../../classes/leave';
import { PlanningHasEntity } from '../../classes/planning-has-entity.class';
import { Planning } from '../../classes/planning.class';
import { Project } from '../../classes/project.class';
import { RealisationHourtype } from '../../classes/realisation';
import { UserPlanning } from '../../classes/user-planning';
import { User } from '../../classes/user.class';
import { CodaltComponent } from '../../codalt.component';
import { EmployeeService } from '../../services/employee.service';
import { ProjectDetailDialogComponent } from '../project-detail-dialog/project-detail-dialog.component';
import { AddLeaveNameDialogComponent, returnData } from './add-leave-name-dialog/add-leave-name-dialog.component';
import { AddProjectDialogComponent } from './add-project-dialog/add-project-dialog.component';
import { EmployeeDayInfoDialogComponent } from './employee-day-info-dialog/employee-day-info-dialog.component';
import { EmployeePlanningMakeKeyPipe } from './employee-planning-make-key.pipe';
import type { BlockedUser } from './employee-planning-user-row/employee-planning-user-row.component';
import { EmployeePlanningUserRowComponent } from './employee-planning-user-row/employee-planning-user-row.component';

const HIRE_ENTITY_IDS: number[] = [EntityId.HiringEmployee, EntityId.HiringMaterials];
/** Returns true when two values refer to the same saved or in-memory item. */
const sameEntityOrSavedId = <T extends { id?: number }>(left: T, right: T): boolean =>
    left === right || (!!left.id && left.id === right.id);
const GROUP_ROLE_PREFIX = /^\(Groep\)\s*/;

/** Projects shown in more than one row (performer variants). */
const multiRowProjectIds = (rows: ProjectDay[]) =>
    new Set(
        rows.filter((row, index) => rows.findIndex(r => r.projectId === row.projectId) !== index).map(r => r.projectId)
    );

@Component({
    selector: 'app-employee-planning',
    templateUrl: './employee-planning.component.html',
    styleUrls: ['./employee-planning.component.scss'],
    providers: [EmployeePlanningMakeKeyPipe],
    standalone: false,
})
export class EmployeePlanningComponent extends CodaltComponent implements OnInit, AfterViewInit, OnDestroy {
    private static readonly planningHoverCellSelector = 'td.day[data-day], th.day[data-day]';
    private static readonly planningHighlightHeaderSelector = 'th.day';

    private cleanupPlanningPointerOver?: () => void;
    private highlightedCells: HTMLElement[] = [];
    private highlightedKey?: string;
    private planningTable?: HTMLTableElement;
    private projectIdsWithMultipleRows = new Set<string>();
    // Empty rows are removed once after the initial load. From then on rows are only added, never hidden again.
    private initialProjectDaysFiltered = false;

    showHelp = false;

    fcNightPlanning = new FormControl();

    fcProjectIds = new FormControl<string[]>();
    fcPerformerIds = new FormControl<number[]>();
    fcRoleKeys = new FormControl<string[]>(
        LocalStorage.getUser()?.settings?.modules?.[USER_SETTINGS_MODULE_KEYS.PERSONNEL_PLANNING]?.uniqueRoleKeys ?? [],
        { nonNullable: true }
    );
    userDatesMap = new Map<number, string[]>();
    @ViewChild('planningTable') planningTableRef?: ElementRef<HTMLTableElement>;
    @ViewChildren(EmployeePlanningUserRowComponent)
    private employeePlanningUserRows: QueryList<EmployeePlanningUserRowComponent>;
    private userRowFingerprints = new Map<number, string>();

    projects: Project[];
    planningList: Planning[];
    usersMap: Map<number, User>;
    entitiesMap: Map<number, Entity>;
    entities: Entity[];
    leaveData: Leave[];
    leaveList: Leave[];
    asfaltploegenModus = false;

    print = false;
    fromDate: Date;
    toDate: Date;
    disableNext = false;
    week: number;
    year: number;

    EntityId = EntityId;
    downloadingPdf = false;

    projectDaysList: ProjectDay[];
    projectDaysListFiltered: ProjectDay[];

    smallMaterial: Entity[];

    addingHiringForEntityId: number;
    entityHiringNames = new Map<number, string[]>();
    hireEntities: Entity[];
    fcAddHiring = new FormControl<string>();
    @ViewChild('addHiringInp') addHiringInput: ElementRef;
    entityWeekUsedDays: Map<string, string[]> = new Map<string, string[]>();
    hiringAutocompleteOptions: string[];
    uniqueProjectsIds: string[];

    readonly zzpHourTypes = [{ type: RealisationHourtype.zzp_leave, label: 'ZZP N/b' }];
    readonly leaveHourTypes = [
        { type: RealisationHourtype.frost, label: 'Spaaruren' },
        { type: RealisationHourtype.time_for_time, label: 'Tijd voor tijd' },
        { type: RealisationHourtype.paid_leave, label: 'Doorbetaald verlof' },
        { type: RealisationHourtype.short_absence, label: 'Kort verzuim' },
        { type: RealisationHourtype.education, label: 'Scholing' },
        { type: RealisationHourtype.bank_holiday, label: 'Feestdag' },
        { type: RealisationHourtype.sleep, label: 'Slapen' },
    ];

    users: User[];
    uniqueRoles: Array<Role & { users: User[] }>;
    groupRoles: Role[];
    filteredUniqueRoles: Array<Role & { users: User[]; groups: Array<{ key: string; name: string; users: User[] }> }>;
    teams: Team[];

    projectManagers: User[];
    executors: User[];
    filteredExecutors: User[];

    projectDayTeamMap: Map<string, Planning> = new Map<string, Planning>();

    projectDayMaterialMap: Map<string, PlanningHasEntity[]> = new Map<string, PlanningHasEntity[]>();

    dayUserMap: Map<string, string> = new Map<string, string>();
    userDayHasNightMap = new Map<string, boolean>();
    userDayConflictMap = new Map<string, boolean>();

    projectDayUserMap: Map<string, ProjectDayUserState> = new Map<string, ProjectDayUserState>();
    userWeekCount: Map<number, number> = new Map<number, number>();
    blockedMap: Map<string, BlockedUser>;

    allUsers: User[];
    leaveDays: { day: Date; dayLetter: string; dayKey: string; dayLabel: string; cssClass: string }[] = [];
    leaveDayUserMap: Map<string, Leave> = new Map<string, Leave>();
    currentHourType: RealisationHourtype;
    contextUser: User;
    contextDay: Date;
    contextComment = 'Personeelsplanning';
    contextProject: Project;

    leavesToSave: Leave[] = [];
    planningsToSave: Planning[] = [];
    private projectDayRowCounter = 0;
    projectDayPerformerChangesToSave: ProjectDayPerformerChange[] = [];
    userPlanningsToSave: UserPlanning[] = [];
    planningHasToSave: PlanningHasEntity[] = [];
    formDirty = false;
    saving = false;
    rollingBack = false;
    userPlannings: UserPlanning[];
    planningHass: PlanningHasEntity[];
    periodSubscriptions: Subscription;

    PlanningStatus = this.environment.status.Planning;
    private personnelPlanningSettings: PersonnelPlanningSettings = {};
    dataLoadInProgress = false;

    readonly = !AuthorisationService.hasFunctionality(Functionalities.EmployeePlanningManagement);

    constructor(
        private confirmDialogService: ConfirmDialogService,
        private planningService: PlanningService,
        private projectService: ProjectService,
        private activatedRoute: ActivatedRoute,
        private userPlanningService: UserPlanningService,
        private afasService: AfasService,
        private planningDetailDialogService: PlanningDetailDialogService,
        private entitiesService: EntitiesService,
        private router: Router,
        private dialog: MatDialog,
        private authService: AuthService,
        private authorisationService: AuthorisationService,
        private userService: UserService,
        private planningHasService: PlanningHasService,
        private ngZone: NgZone,
        private leaveService: LeaveService,
        protected employeeService: EmployeeService,
        private makeKeyPipe: EmployeePlanningMakeKeyPipe
    ) {
        super();
    }

    private filterProjectDaysList(): void {
        if (!this.projectDaysList) {
            this.projectDaysListFiltered = [];
            this.filteredExecutors = [];
            this.projectIdsWithMultipleRows.clear();
            return;
        }

        const projectIds = this.fcProjectIds.value ?? [];
        const performerIds = this.fcPerformerIds.value ?? [];
        const roleKeys = this.fcRoleKeys.value ?? [];
        const uniqueRoleKeys = roleKeys.filter(roleKey => this.uniqueRoles?.some(role => role.key === roleKey));
        const selectedGroupRoles = (this.groupRoles ?? []).filter(role => roleKeys.includes(role.key));
        // Each unique-role section is split into group-role subsections; a user only appears under the first match
        this.filteredUniqueRoles = (this.uniqueRoles ?? [])
            .filter(role => !uniqueRoleKeys.length || uniqueRoleKeys.includes(role.key))
            .map(role => {
                const seenUserIds = new Set<number>();
                const groups = (selectedGroupRoles.length ? selectedGroupRoles : (this.groupRoles ?? []))
                    .map(groupRole => {
                        const users = role.users.filter(
                            user => !seenUserIds.has(user.id) && user.roles?.some(r => r.key === groupRole.key)
                        );
                        users.forEach(user => seenUserIds.add(user.id));
                        return { key: groupRole.key, name: groupRole.name.replace(GROUP_ROLE_PREFIX, ''), users };
                    })
                    .filter(group => group.users.length);
                const otherUsers = selectedGroupRoles.length
                    ? []
                    : role.users.filter(user => !seenUserIds.has(user.id));
                if (otherUsers.length) {
                    groups.push({ key: '', name: groups.length ? 'Overig' : '', users: otherUsers });
                }
                return { ...role, users: groups.flatMap(group => group.users), groups };
            })
            .filter(role => role.users.length);

        // Filter the initial rows by actual personnel-planning data. Later refreshes only apply the user-selected
        // project and performer filters, so manually added and websocket-added rows remain visible.
        if (!this.initialProjectDaysFiltered && this.projectDaysList.length) {
            const projectIdsWithMultipleRows = multiRowProjectIds(this.projectDaysList);
            const { projectIds: projectIdsWithPlanning, rowKeys } = this.getUserPlanningVisibility();
            this.projectDaysList = this.projectDaysList.filter(
                projectDays =>
                    projectIdsWithPlanning.has(projectDays.projectId) &&
                    (!projectIdsWithMultipleRows.has(projectDays.projectId) || rowKeys.has(projectDays.rowKey))
            );
            this.initialProjectDaysFiltered = true;
        }

        // Cache projects with variants once. Material-cell lookups use this to avoid scanning all project rows
        // during every change-detection pass while still preventing fallback into a different performer row.
        this.projectIdsWithMultipleRows = multiRowProjectIds(this.projectDaysList);

        const filteredProjectDaysList = this.projectDaysList
            .filter(pdl => {
                const performerId = pdl.form.controls.performer_id.value ?? pdl.project?.performer_id ?? null;
                return (
                    (!projectIds.length || projectIds.includes(pdl.projectId)) &&
                    (!performerIds.length || performerIds.includes(performerId))
                );
            })
            .sort((a, b) => a.projectId.localeCompare(b.projectId, undefined, { numeric: true }));
        const filteredProjectDaysChanged =
            this.projectDaysListFiltered?.length !== filteredProjectDaysList.length ||
            filteredProjectDaysList.some(
                (projectDays, index) => projectDays.rowKey !== this.projectDaysListFiltered[index]?.rowKey
            );
        if (filteredProjectDaysChanged) {
            // Preserve the input reference so OnPush employee rows stay skipped when visible projects did not change.
            this.projectDaysListFiltered = filteredProjectDaysList;
        }

        this.filteredExecutors = (this.executors ?? []).filter(e =>
            this.projectDaysListFiltered.some(
                pdl => (pdl.form.controls.performer_id.value ?? pdl.project?.performer_id) === e.id
            )
        );
    }

    ngOnInit(): void {
        this.personnelPlanningSettings =
            LocalStorage.getUser()?.settings?.modules?.[USER_SETTINGS_MODULE_KEYS.PERSONNEL_PLANNING] ?? {};
        this.subscriptions.add(
            // Refresh the cached current user from the server so this page always opens with the latest preferences.
            this.authService.getUserDetails().subscribe(response => {
                this.personnelPlanningSettings =
                    response.settings?.modules?.[USER_SETTINGS_MODULE_KEYS.PERSONNEL_PLANNING] ?? {};
                this.fcRoleKeys.setValue(this.personnelPlanningSettings.uniqueRoleKeys ?? [], { emitEvent: false });
                this.filterProjectDaysList();
            })
        );
        this.subscriptions.add(
            this.activatedRoute.queryParams.subscribe(q => {
                this.print = q.print === 'true';
            })
        );
        this.subscriptions.add(
            merge(this.fcPerformerIds.valueChanges, this.fcProjectIds.valueChanges, this.fcRoleKeys.valueChanges)
                .pipe(distinctUntilChanged())
                .pipe(debounceTime(100))
                .subscribe(() => {
                    this.filterProjectDaysList();
                })
        );
        this.subscriptions.add(
            this.fcRoleKeys.valueChanges.subscribe(roleKeys => {
                this.userService
                    .saveUserSettings(USER_SETTINGS_MODULE_KEYS.PERSONNEL_PLANNING, {
                        ...this.personnelPlanningSettings,
                        uniqueRoleKeys: roleKeys,
                    })
                    .subscribe(response => {
                        this.personnelPlanningSettings =
                            response.data.modules?.[USER_SETTINGS_MODULE_KEYS.PERSONNEL_PLANNING] ?? {};
                        const user = LocalStorage.getUser();
                        if (user) {
                            LocalStorage.setUser({
                                ...user,
                                settings: response.data,
                            });
                        }
                    });
            })
        );
        this.subscriptions.add(
            this.activatedRoute.params.subscribe(params => {
                this.dataLoadInProgress = true;
                this.clearMaps();
                const monday = new Date();
                if (params?.year) {
                    monday.setFullYear(params.year);
                }
                Utils.setTime(monday, 0, 0);
                const year = monday.getFullYear();
                monday.setMonth(0);
                monday.setDate(4);
                monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
                const beginDate = new Date(monday);

                const week = +(params.week ?? Utils.formatDate(new Date(), 'w'));
                beginDate.setDate(beginDate.getDate() + 7 * (week - 1));

                const endDate = Utils.setTime(new Date(beginDate), 23, 59);
                endDate.setDate(endDate.getDate() + 6);

                this.week = week;
                this.year = week > 1 ? beginDate.getFullYear() : endDate.getFullYear();

                this.fromDate = beginDate;
                this.toDate = endDate;

                if (this.readonly) {
                    const maxDate = new Date();
                    // When after thursday 15:00, next week pls
                    if (maxDate.getDay() > 4 || (maxDate.getDay() === 4 && maxDate.getHours() > 15)) {
                        maxDate.setDate(maxDate.getDate() + 7);
                    }
                    Utils.setTime(maxDate, 23, 59);
                    maxDate.setDate(maxDate.getDate() + (7 - maxDate.getDay()));
                    if (maxDate < this.toDate) {
                        this.prev();
                    } else {
                        this.getData();
                    }
                    this.disableNext = Utils.dateFormatEquals(maxDate, this.toDate, 'yyyy-MM-dd');
                } else {
                    this.getData();
                }

                this.leaveDays = [];
                const loopDate = new Date(this.fromDate);
                while (loopDate.getTime() < this.toDate.getTime()) {
                    const day = Utils.setTime(new Date(loopDate), 0, 0);
                    const dayLabel = Utils.formatDate(day, 'E d MMM y');
                    this.leaveDays.push({
                        day,
                        dayLetter: dayLabel.charAt(0),
                        dayKey: Utils.formatDate(day),
                        dayLabel,
                        cssClass: 'day day-' + dayLabel.charAt(0),
                    });
                    loopDate.setDate(loopDate.getDate() + 1);
                }
            })
        );
    }

    ngAfterViewInit(): void {
        this.setupPlanningPointerOverListener();
    }

    ngOnDestroy() {
        this.teardownPlanningPointerOverListener();
        this.periodSubscriptions?.unsubscribe();
        super.ngOnDestroy();
    }

    @HostListener('window:beforeunload', ['$event'])
    unloadNotification($event: any) {
        if (this.hasUnsavedChanges) {
            $event.returnValue = true;
        }
    }

    private get hasUnsavedChanges(): boolean {
        return this.hasPendingChanges || !!this.planningsToSave?.length || this.formDirty;
    }

    /** Changes the undo/save buttons act on (incl. leave, excl. new team plannings). */
    get hasPendingChanges(): boolean {
        return !!(
            this.userPlanningsToSave?.length ||
            this.planningHasToSave?.length ||
            this.leavesToSave?.length ||
            this.projectDayPerformerChangesToSave?.length
        );
    }

    openChangesBackActionCheck(): Promise<boolean> {
        if (!this.hasUnsavedChanges) {
            return Promise.resolve(true);
        }
        return this.confirmDialogService
            .confirm({
                title: 'Niet opgeslagen wijzigingen',
                text: `Wilt u de niet opgeslagen wijzigingen verwerpen?`,
                confirm: 'Hier blijven',
                cancel: 'Wijzigingen verwerpen',
            })
            .then(
                () => false,
                () => {
                    this.userPlanningsToSave = [];
                    this.planningHasToSave = [];
                    this.planningsToSave = [];
                    this.leavesToSave = [];
                    this.projectDayPerformerChangesToSave = [];
                    this.formDirty = false;
                    return true;
                }
            );
    }

    makeKey(date: Date | string, ...parts: Array<string | number | null | undefined>) {
        return this.makeKeyPipe.transform(date, ...parts);
    }

    private get allProjectIds(): string[] {
        return [
            ...new Set([
                ...this.projects.map(p => p.afas_project_id),
                ...this.planningList.map(p => p.afas_project_id ?? p.worknumber),
            ]),
        ];
    }

    /** Default day- or night-start (per toggle) plus the given duration. */
    private setDefaultTimes(fromDate: Date, toDate: Date, durationHours: number): void {
        const startTime = 60 * (this.fcNightPlanning.value ? Settings.DEFAULT_START_NIGHT : Settings.DEFAULT_START);
        fromDate.setMinutes(startTime);
        toDate.setMinutes(startTime + 60 * durationHours);
    }

    /** Drops unnecessary create+delete pairs and duplicate updates from the mutations to save. */
    private normalizeUserPlanningsToSave(): void {
        const normalized: UserPlanning[] = [];

        for (const userPlanning of this.userPlanningsToSave) {
            // Unsaved create + delete cancels out.
            if (!userPlanning.id && userPlanning.deleted_at) {
                continue;
            }

            const existingMutation = userPlanning.id
                ? normalized.find(existing => existing.id === userPlanning.id)
                : normalized.find(
                      existing => !existing.id && this.isSameExactUserPlanningMutation(existing, userPlanning)
                  );

            if (existingMutation) {
                Object.assign(existingMutation, userPlanning);

                if (!existingMutation.id && existingMutation.deleted_at) {
                    normalized.splice(normalized.indexOf(existingMutation), 1);
                }

                continue;
            }

            const inverseMutation = normalized.find(
                existing =>
                    this.isSameExactUserPlanningMutation(existing, userPlanning) &&
                    !!existing.deleted_at !== !!userPlanning.deleted_at
            );

            if (inverseMutation) {
                normalized.splice(normalized.indexOf(inverseMutation), 1);
                continue;
            }

            normalized.push(userPlanning);
        }

        this.userPlanningsToSave = normalized;
    }

    private isSameUserPlanningTimeSlot(a: UserPlanning, b: UserPlanning): boolean {
        return a.user_id === b.user_id && Utils.sameDateRange(a, b);
    }

    /**
     * Identity for a single user-planning mutation without using planning_id.
     * This keeps delete+re-add on the same visible cell cancellable, while allowing
     * a delete on one project and a create on another project for the same time slot.
     */
    private isSameExactUserPlanningMutation(a: UserPlanning, b: UserPlanning): boolean {
        return (
            this.isSameUserPlanningTimeSlot(a, b) &&
            (a.afas_project_id ?? null) === (b.afas_project_id ?? null) &&
            (a.performer_id ?? null) === (b.performer_id ?? null)
        );
    }

    /** Saved, not (queued for) deleted user planning with exactly the same slot, project, performer and planning. */
    private findActiveExistingUserPlanning(candidate: UserPlanning): UserPlanning | undefined {
        const ignoredDeletedIds = new Set(
            (this.userPlanningsToSave ?? [])
                .filter(userPlanning => !!userPlanning.id && !!userPlanning.deleted_at)
                .map(userPlanning => userPlanning.id)
        );

        return this.userPlannings?.find(
            existing =>
                !!existing.id &&
                !existing.deleted_at &&
                !ignoredDeletedIds.has(existing.id) &&
                this.isSameExactUserPlanningMutation(existing, candidate) &&
                (existing.planning_id ?? null) === (candidate.planning_id ?? null)
        );
    }

    private showAlreadyPlannedOnDayDialog(subject: 'De persoon' | 'Deze ploeg'): void {
        this.confirmDialogService.confirm({
            title: 'Al ingepland op deze dag',
            text: `${subject} is op deze dag al ingepland en kan daarom alleen via de transport- of weekplanning ingepland worden.`,
            confirm: 'Sluiten',
            dialogOptions: {
                disableClose: false,
            },
        });
    }

    /** performer_id, or in Track Line the voorman of the linked weekplanning. */
    private itemPerformerId(item: { performer_id?: number; planning_id?: number }): number | undefined {
        return (
            item.performer_id ??
            (this.environment.platform === 'trackline'
                ? this.planningList?.find(planning => planning.id === item.planning_id)?.performer
                : undefined)
        );
    }

    /** Row fields; Track Line fills them in the weekplanning, so take the week's first planning of the (first) voorman. */
    private rowProjectFields(
        projectId: string,
        project?: Project,
        performerId?: number
    ): Pick<Project, 'performer_id' | 'location' | 'projectmanager_id'> {
        const planning =
            this.environment.platform === 'trackline'
                ? this.planningList?.find(
                      p =>
                          (p.afas_project_id ?? p.worknumber) === projectId &&
                          (performerId == null ? !!p.performer : p.performer === performerId)
                  )
                : undefined;
        return {
            performer_id: performerId ?? planning?.performer ?? project?.performer_id,
            // AFAS only fills location_place, so fall back to it when the project has no location.
            location: planning?.location ?? (project?.location || project?.location_place),
            projectmanager_id: planning?.projectmanager ?? project?.projectmanager_id,
        };
    }

    /**
     * Picks the visible project row that should represent a performer.
     * The last matching row can be preferred for unsaved project-only planning so edits stay in the clicked column.
     */
    private getProjectDayForPerformer(
        projectId?: string,
        performerId?: number | null,
        preferLastMatchingRow = false
    ): ProjectDay | undefined {
        if (!projectId) {
            return undefined;
        }

        const projectRows = this.projectDaysList?.filter(projectDay => projectDay.projectId === projectId) ?? [];

        if (performerId != null) {
            const performerRows = projectRows.filter(
                projectDay => projectDay.form.controls.performer_id.value === performerId
            );

            if (performerRows.length) {
                return preferLastMatchingRow ? performerRows[performerRows.length - 1] : performerRows[0];
            }
        }

        return projectRows[0];
    }

    /**
     * Finds the performer that should own a material assignment in this view.
     * When the backend did not persist `performer_id`, we infer it from the driver's user planning on that day.
     */
    private resolvePlanningHasPerformerId(
        planningHas: PlanningHasEntity,
        projectId?: string,
        userPlannings: UserPlanning[] = this.userPlannings ?? []
    ): number | undefined {
        const performerId = this.itemPerformerId(planningHas);
        if (performerId != null) {
            return performerId;
        }

        if (!planningHas.driver_user_id || !projectId) {
            return undefined;
        }

        const planningDayKey = Utils.formatDate(planningHas.begindate);

        return userPlannings.find(
            userPlanning =>
                !userPlanning.deleted_at &&
                userPlanning.user_id === planningHas.driver_user_id &&
                userPlanning.performer_id != null &&
                this.resolveProjectId(userPlanning) === projectId &&
                Utils.formatDate(userPlanning.begindate) === planningDayKey
        )?.performer_id;
    }

    /**
     * Builds the possible material-map keys for one visible cell or, when requested, for every row of a project.
     * That keeps all material-specific row logic in one place instead of spreading row-key fallbacks around.
     */
    private getProjectDayMaterialMapKeys(
        day: Date | string,
        entityId: number,
        {
            projectId,
            nonUseOnceName,
            performerId,
            preferLastMatchingRow = false,
            includeAllProjectRows = false,
        }: {
            projectId?: string;
            nonUseOnceName?: string | null;
            performerId?: number | null;
            preferLastMatchingRow?: boolean;
            includeAllProjectRows?: boolean;
        }
    ): string[] {
        const identifiers = includeAllProjectRows
            ? [
                  ...(this.projectDaysList
                      ?.filter(projectDay => projectDay.projectId === projectId)
                      .map(projectDay => projectDay.rowKey) ?? []),
                  projectId,
              ]
            : [this.getProjectDayForPerformer(projectId, performerId, preferLastMatchingRow)?.rowKey, projectId];

        return [...new Set(identifiers.filter(Boolean))].map(identifier =>
            this.makeKey(day, entityId, identifier, nonUseOnceName)
        );
    }

    /**
     * Returns the first material entry that belongs to the addressed row.
     * Project-level fallback is only safe when no performer variants exist.
     */
    getProjectDayMaterialEntry(
        day: Date | string,
        entityId: number,
        projectId?: string,
        rowKey?: string,
        nonUseOnceName?: string | null,
        performerId?: number | null,
        preferLastMatchingRow = false
    ): PlanningHasEntity[] | undefined {
        if (rowKey) {
            // Grid cells already know their row key. Direct lookups avoid rebuilding candidate-key arrays and
            // rescanning every project row for each of the thousands of material cells during change detection.
            const rowMaterial = this.projectDayMaterialMap.get(this.makeKey(day, entityId, rowKey, nonUseOnceName));
            if (rowMaterial?.length) {
                return rowMaterial;
            }
            if (!projectId || this.projectIdsWithMultipleRows.has(projectId)) {
                return undefined;
            }

            const projectMaterial = this.projectDayMaterialMap.get(
                this.makeKey(day, entityId, projectId, nonUseOnceName)
            );
            return projectMaterial?.length ? projectMaterial : undefined;
        }

        return this.getProjectDayMaterialMapKeys(day, entityId, {
            projectId,
            nonUseOnceName,
            performerId,
            preferLastMatchingRow,
        })
            .map(materialKey => this.projectDayMaterialMap.get(materialKey))
            .find(material => !!material?.length);
    }

    private setupPlanningPointerOverListener() {
        const table = this.planningTableRef?.nativeElement;
        if (!table) {
            this.teardownPlanningPointerOverListener();
            return;
        }

        if (this.planningTable === table && this.cleanupPlanningPointerOver) {
            return;
        }

        this.cleanupPlanningPointerOver?.();
        this.clearPlanningHighlight();
        this.planningTable = table;

        this.ngZone.runOutsideAngular(() => {
            const handlePointerOver = (event: PointerEvent) => this.handlePlanningPointerOver(event);
            const handlePointerLeave = () => this.clearPlanningHighlight();

            table.addEventListener('pointerover', handlePointerOver, { passive: true });
            table.addEventListener('pointerleave', handlePointerLeave, { passive: true });
            this.cleanupPlanningPointerOver = () => {
                table.removeEventListener('pointerover', handlePointerOver);
                table.removeEventListener('pointerleave', handlePointerLeave);
            };
        });
    }

    private teardownPlanningPointerOverListener() {
        this.cleanupPlanningPointerOver?.();
        this.cleanupPlanningPointerOver = undefined;
        this.planningTable = undefined;
        this.clearPlanningHighlight();
    }

    private handlePlanningPointerOver(event: PointerEvent): void {
        const cell = (event.target as HTMLElement | null)?.closest<HTMLElement>(
            EmployeePlanningComponent.planningHoverCellSelector
        );

        if (!cell) {
            return;
        }

        this.setPlanningHighlight(cell.dataset['day'], cell.dataset['rowKey'] ?? '');
    }

    private setPlanningHighlight(day?: string, projectId = ''): void {
        if (!day) {
            this.clearPlanningHighlight();
            return;
        }

        const highlightKey = `${day}|${projectId}`;
        if (this.highlightedKey === highlightKey) {
            return;
        }

        const table = this.planningTableRef?.nativeElement;
        if (!table) {
            this.clearPlanningHighlight();
            return;
        }

        this.clearPlanningHighlight();

        const selector = `${EmployeePlanningComponent.planningHighlightHeaderSelector}[data-day="${day}"][data-row-key="${projectId}"]`;
        this.highlightedCells = Array.from(table.querySelectorAll<HTMLElement>(selector));
        this.highlightedCells.forEach(cell => cell.classList.add('highlight'));
        this.highlightedKey = highlightKey;
    }

    private clearPlanningHighlight(): void {
        this.highlightedCells.forEach(cell => cell.classList.remove('highlight'));
        this.highlightedCells = [];
        this.highlightedKey = undefined;
    }

    addEntityPlanning(
        event: Event,
        day: { day: Date; project?: Project },
        entity: Entity,
        nonUseOnceName?: string,
        silent?: boolean,
        rowKey?: string,
        performerId?: number
    ) {
        event.preventDefault();
        // ProjectDay also contains disabled cells whose project has not resolved yet; those cells cannot be saved.
        if (this.readonly || !day.project) {
            return;
        }

        const selectedPerformerId = performerId ?? this.rowPerformerId(rowKey);
        const planningHasList = this.getProjectDayMaterialEntry(
            day.day,
            entity.id,
            day.project?.afas_project_id,
            rowKey,
            nonUseOnceName,
            selectedPerformerId
        );
        const teamEntity = this.teams.find(team =>
            this.projectDayTeamMap.has(this.makeKey(day.day, team.entity.id, day.project?.afas_project_id))
        )?.entity;
        const planning = this.projectDayTeamMap.get(
            this.makeKey(day.day, teamEntity?.id, day.project?.afas_project_id)
        );

        if (planningHasList?.length) {
            if (planningHasList?.length > 1 && !Utils.pheSame(planningHasList[0], planningHasList[1])) {
                if (!silent) {
                    this.confirmDialogService.confirm({
                        title: 'Meer keren ingepland',
                        text: `Dit materieel is op deze dag meerdere keren ingepland en kan daarom alleen via de transport of weekplanning aangepast worden.`,
                        confirm: 'Sluiten',
                    });
                }
                return;
            } else if (!planningHasList[0].planning_id && !!planning && entity.entitytypes[0].visible_transport) {
                planningHasList[0].planning_id = planning.id;
                planningHasList[0].planning = planning;
                planningHasList[0].afas_project_id = undefined;
                if (!this.planningHasToSave.includes(planningHasList[0])) {
                    this.planningHasToSave.push(planningHasList[0]);
                }
            } else {
                let reAddUserPlanningToUser = null;
                if (planningHasList[0].driver_user_id) {
                    const user = this.usersMap.get(planningHasList[0].driver_user_id);
                    // don't remove user, add as normal userPlanning
                    this.addUserPlanningProject(
                        event,
                        day,
                        this.getProjectDayEntry(day.day, user.id, day.project?.afas_project_id, rowKey),
                        user
                    );
                    reAddUserPlanningToUser = user;
                }
                planningHasList[0].deleted_at = new Date();

                if (!this.planningHasToSave.includes(planningHasList[0])) {
                    this.planningHasToSave.push(planningHasList[0]);
                }
                if (Utils.pheSame(planningHasList[0], planningHasList[1])) {
                    planningHasList[1].deleted_at = new Date();

                    if (!this.planningHasToSave.includes(planningHasList[1])) {
                        this.planningHasToSave.push(planningHasList[1]);
                    }
                }
                this.rebuildView();
                if (reAddUserPlanningToUser) {
                    this.addUserPlanningProject(
                        event,
                        day,
                        this.getProjectDayEntry(
                            day.day,
                            reAddUserPlanningToUser.id,
                            day.project?.afas_project_id,
                            rowKey
                        ),
                        reAddUserPlanningToUser,
                        true
                    );
                }
            }
        } else {
            const projectsIds = this.allProjectIds;
            if (
                projectsIds.some(projectId =>
                    this.getProjectDayMaterialMapKeys(day.day, entity.id, {
                        projectId,
                        nonUseOnceName,
                        includeAllProjectRows: true,
                    }).some(materialKey => this.projectDayMaterialMap.has(materialKey))
                )
            ) {
                if (!silent) {
                    this.confirmDialogService.confirm({
                        title: 'Al ingepland op deze dag',
                        text: nonUseOnceName
                            ? `Deze persoon is al ingehuurd op deze dag. Dubbele inhuur is niet mogelijk.`
                            : `Het materieel is op deze dag al ingepland en kan daarom
                    alleen via de transport- of weekplanning ingepland worden.`,
                        confirm: 'Sluiten',
                    });
                }
                return;
            }

            const fromDate = planning ? new Date(Utils.mainPlanning(planning).begindate) : new Date(day.day);
            const toDate = planning ? new Date(Utils.mainPlanning(planning).enddate) : new Date(day.day);
            if (
                !planning ||
                (planning &&
                    this.fcNightPlanning.value &&
                    Math.abs(fromDate.getHours() - Settings.DEFAULT_START_NIGHT) > 3)
            ) {
                this.setDefaultTimes(fromDate, toDate, Settings.DEFAULT_DURATION_OTHER);
            }

            const planningHas = new PlanningHasEntity();
            planningHas.entity_id = entity.id;
            planningHas.entitytype_key = entity.entitytypes[0].key;
            planningHas.entitytype = entity.entitytypes[0];
            planningHas.begindate = fromDate;
            planningHas.enddate = toDate;
            if (planning && HIRE_ENTITY_IDS.includes(entity.id)) {
                planningHas.planning = planning;
                planningHas.planning_id = planning.id;
            } else {
                planningHas.afas_project_id = day.project.afas_project_id;
            }
            planningHas.name = nonUseOnceName;
            if (selectedPerformerId != null) {
                planningHas.performer_id = selectedPerformerId;
            }
            if (entity.driver_user_id) {
                const userKey = this.makeKey(
                    day.day,
                    entity.driver_user_id,
                    rowKey ??
                        this.getProjectDayForPerformer(day.project?.afas_project_id, selectedPerformerId)?.rowKey ??
                        day.project?.afas_project_id
                );
                const projectDayUser = this.projectDayUserMap.get(userKey);
                if (projectDayUser) {
                    // remove user planning
                    const user = this.usersMap.get(entity.driver_user_id);
                    this.addUserPlanningProject(
                        event,
                        day,
                        this.projectDayUserMap.get(userKey),
                        user,
                        false,
                        selectedPerformerId,
                        rowKey
                    );
                    this.assignDriverToMaterialPlanning(user, planningHas, selectedPerformerId);
                }
            }

            this.planningHasToSave.push(planningHas);
        }
        this.refreshPlanningView();
        return true;
    }

    addProjectDayVariant(sourceProjectDays: ProjectDay, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();

        if (this.readonly) {
            return;
        }

        const sourceExecutorId = sourceProjectDays.form.controls.performer_id.value;
        if (!sourceExecutorId) {
            this.confirmDialogService.confirm({
                title: 'Uitvoerder ontbreekt',
                text: `Selecteer eerst een uitvoerder voor project ${sourceProjectDays.projectId}.`,
                confirm: 'Sluiten',
            });
            return;
        }

        const dialogData = {
            title: 'Extra inplanning toevoegen',
            message: `Kies een uitvoerder voor een extra inplanning op project ${sourceProjectDays.projectId}.`,
            confirmButtonText: 'Toevoegen',
            availableExecutors: this.executors,
            sourceExecutor: this.executors.find(e => e.id === sourceExecutorId),
        };

        const dialogRef = this.dialog.open(SelectExecutorDialogComponent, { data: dialogData });

        this.periodSubscriptions.add(
            dialogRef.afterClosed().subscribe(result => {
                if (!result?.selectedExecutor) {
                    return;
                }

                const projectDays = this.createProjectDays(
                    sourceProjectDays.projectId,
                    [sourceProjectDays.project].filter(p => !!p)
                );
                projectDays.form.patchValue(
                    { ...sourceProjectDays.form.getRawValue(), performer_id: result.selectedExecutor.id },
                    { emitEvent: false }
                );
                projectDays.syncedPerformerId = result.selectedExecutor.id;

                const sourceIndex = this.projectDaysList.findIndex(pd => pd.rowKey === sourceProjectDays.rowKey);
                if (sourceIndex !== -1) {
                    this.projectDaysList.splice(sourceIndex + 1, 0, projectDays);
                } else {
                    this.projectDaysList.push(projectDays);
                }
                this.refreshPlanningView();
            })
        );
    }

    addProject() {
        const ref = this.dialog.open(AddProjectDialogComponent, {
            // Global dialog defaults omit autoFocus. Move focus before Material hides the page with aria-hidden.
            autoFocus: 'input',
            maxWidth: '400px',
            width: '100vw',
            maxHeight: '100%',
            disableClose: false,
            panelClass: 'comment-edit-dialog',
            data: { currentProjects: this.projectDaysListFiltered?.map(pdl => pdl.projectId) ?? [] },
        });
        this.periodSubscriptions.add(
            ref.afterClosed().subscribe(project => {
                if (project) {
                    // Hidden base rows are absent from the dialog's current-project list. Reuse one instead of
                    // creating a temporary duplicate that becomes visible after the websocket refresh.
                    const existingProjectDays = this.projectDaysList?.find(
                        projectDays =>
                            projectDays.projectId === project.afas_project_id &&
                            projectDays.form.controls.performer_id.value == null
                    );
                    const projectDays =
                        existingProjectDays ?? this.createProjectDays(project.afas_project_id, [project]);
                    if (!this.uniqueProjectsIds.includes(project.afas_project_id)) {
                        this.uniqueProjectsIds.push(project.afas_project_id);
                        this.uniqueProjectsIds = this.uniqueProjectsIds.sort((a, b) =>
                            a.localeCompare(b, undefined, { numeric: true })
                        );
                    }
                    if (!this.projectDaysList) {
                        this.projectDaysList = [];
                    }
                    if (!existingProjectDays) {
                        this.projectDaysList.splice(
                            this.uniqueProjectsIds.indexOf(project.afas_project_id),
                            0,
                            projectDays
                        );
                    }
                    this.refreshPlanningView();
                    requestAnimationFrame(() => this.setupPlanningPointerOverListener());
                }
            })
        );
    }

    addTeamPlanning(
        event,
        day: {
            day: Date;
            project?: Project;
        },
        team: Team,
        rowKey?: string
    ) {
        event.preventDefault();
        const project = day.project;
        if (this.readonly || !project) {
            return;
        }
        const resolvedDay = { day: day.day, project };
        const key = this.makeKey(day.day, team.entity.id, day.project?.afas_project_id);
        let planning = this.projectDayTeamMap.get(key);
        if (planning && planning.user_planning?.length) {
            if (!planning?.id) {
                this.planningsToSave.splice(this.planningsToSave.indexOf(planning), 1);
            }
            planning.user_planning.forEach(up => {
                const key = this.makeKey(day.day, up.user_id, rowKey ?? day.project?.afas_project_id);
                if (this.projectDayUserMap.has(key)) {
                    if (!planning?.id) {
                        this.userPlanningsToSave.splice(this.userPlanningsToSave.indexOf(up), 1);
                    } else {
                        up.deleted_at = new Date();
                        this.userPlanningsToSave.push(up);
                    }
                    this.normalizeUserPlanningsToSave();
                }
            });
            planning.user_planning = [];
        } else {
            const projectsIds = this.allProjectIds;
            planning = this.getOrCreateTeamPlanning(planning, team, resolvedDay);
            if (!planning) {
                return;
            }

            const begindate = new Date(Utils.mainPlanning(planning).begindate);
            const enddate = new Date(Utils.mainPlanning(planning).enddate);
            const allRowKeys = (this.projectDaysList ?? []).map(pd => pd.rowKey);
            const performerId = this.rowPerformerId(rowKey);

            team.users.forEach(user => {
                if (
                    !this.projectDayUserMap.has(
                        this.makeKey(day.day, user.id, rowKey ?? day.project?.afas_project_id)
                    ) &&
                    !projectsIds.some(projectId =>
                        this.projectDayUserMap.has(this.makeKey(day.day, user.id, projectId))
                    ) &&
                    !allRowKeys.some(rk => this.projectDayUserMap.has(this.makeKey(day.day, user.id, rk)))
                ) {
                    this.addUserPlanningToPlanningTeam(
                        planning,
                        user,
                        begindate,
                        enddate,
                        resolvedDay,
                        rowKey,
                        performerId
                    );
                }
            });
        }
        this.refreshPlanningView();
    }

    /** Existing team planning, or a new one; undefined when a use-once team is already planned on another project. */
    private getOrCreateTeamPlanning(
        planning: Planning | undefined,
        team: Team,
        day: { day: Date; project: Project }
    ): Planning | undefined {
        if (planning) {
            return planning;
        }
        if (
            this.allProjectIds.some(
                projectId =>
                    !!this.projectDayTeamMap.get(this.makeKey(day.day, team.entity.id, projectId))?.mainPlanning?.entity
                        ?.use_once
            )
        ) {
            this.showAlreadyPlannedOnDayDialog('Deze ploeg');
            return undefined;
        }
        return this.createPlanning(team.entity, team.entityType, day);
    }

    private rowPerformerId(rowKey?: string): number | undefined {
        return rowKey
            ? (this.projectDaysList.find(pd => pd.rowKey === rowKey)?.form.controls.performer_id.value ?? undefined)
            : undefined;
    }

    private createPlanning(entity: Entity, entityType: EntityType, day: { day: Date; project: Project }) {
        const planning = this.planningService.newPlanningByType(entityType.key);
        const mainPlanning = this.planningService.getMainPlanning(planning);
        const fromDate = new Date(day.day);
        const toDate = new Date(day.day);
        this.setDefaultTimes(fromDate, toDate, Settings.DEFAULT_DURATION_TEAM);
        mainPlanning.begindate = fromDate;
        mainPlanning.enddate = toDate;
        mainPlanning.entity = entity;
        mainPlanning.entity_id = entity.id;
        planning.afas_project_id = day.project.afas_project_id;
        planning.worknumber = day.project.afas_project_id;
        planning.location = day.project.location;
        planning.performer = day.project.performer_id;
        planning.projectmanager = day.project.projectmanager_id;
        planning.asphalt_performer = day.project.asphalt_performer_id;
        planning.contractor = day.project.contractor;
        planning.status_key = this.PlanningStatus.PlanningDefinitief;
        planning.user_planning = [];
        this.planningsToSave.push(planning);
        return planning;
    }

    private addUserPlanningToPlanningTeam(
        planning: Planning,
        user: User,
        begindate: Date,
        enddate: Date,
        day: { day: Date; project: Project },
        rowKey?: string,
        performerId?: number
    ) {
        const userPlanning = new UserPlanning();
        userPlanning.planning_id = planning.id;
        userPlanning.planning = planning;
        userPlanning.afas_project_id = day.project?.afas_project_id;
        userPlanning.user_id = user.id;
        userPlanning.begindate = begindate;
        userPlanning.enddate = enddate;
        if (performerId) {
            userPlanning.performer_id = performerId;
        }

        if (this.userHasPlanningOnDay(day.day, user.id, day.project?.afas_project_id, rowKey, begindate, enddate)) {
            return;
        }

        this.storeUserPlanningOnProjectDay(userPlanning, planning);
        this.refreshPlanningView();
    }

    public getTeamCountForDay(dayKey: string, team: { entity: Entity; users: User[] }, rowKey: string) {
        const count = team.users.filter(u => this.projectDayUserMap.has(this.makeKey(dayKey, u.id, rowKey))).length;
        return count || '';
    }

    /**
     * Queues removal of a single user planning and removes its current row entry from the view maps.
     * Optionally removes it from the linked planning object as well.
     */
    private removeSingleUserPlanningFromProjectDay(projectDayUser?: ProjectDayUserState, planning?: Planning): void {
        const userPlanning = projectDayUser?.userPlanning?.[0];
        if (!userPlanning) {
            return;
        }

        userPlanning.deleted_at = new Date();
        if (!this.userPlanningsToSave.includes(userPlanning)) {
            this.userPlanningsToSave.push(userPlanning);
        }
        this.normalizeUserPlanningsToSave();

        if (planning?.user_planning) {
            planning.user_planning.splice(planning.user_planning.indexOf(userPlanning), 1);
        }
    }

    /**
     * Writes one material assignment as the driver's active planning for this row and keeps every related map in sync.
     */
    private assignDriverToMaterialPlanning(user: User, planningHas: PlanningHasEntity, performerId?: number): void {
        planningHas.driver_user_id = user.id;
        planningHas.performer_id = performerId ?? undefined;
        if (!this.planningHasToSave.includes(planningHas)) {
            this.planningHasToSave.push(planningHas);
        }
    }

    addUserPlanningTeam(
        event,
        day: {
            day: Date;
            project?: Project;
        },
        team: Team,
        user: User,
        rowKey?: string
    ) {
        event.preventDefault();
        const project = day.project;
        if (this.readonly || !project) {
            return;
        }
        const resolvedDay = { day: day.day, project };

        const userProjectDay = this.projectDayUserMap.get(
            this.makeKey(day.day, user.id, rowKey ?? day.project?.afas_project_id)
        );

        if (userProjectDay?.type === 'planning') {
            this.confirmDialogService.confirm({
                title: 'Chauffeurs kunnen alleen via de transportplanning geplant worden',
                text: `Deze persoon is op deze dag al ingepland als chauffeur en kan daarom
                    alleen via de transport- of weekplanning ingepland worden.`,
                confirm: 'Sluiten',
            });
            return;
        }

        if (userProjectDay && (userProjectDay.userPlanning?.length || userProjectDay.planningHas?.length)) {
            if (userProjectDay.userPlanning?.length === 1) {
                const planning = this.projectDayTeamMap.get(
                    this.makeKey(day.day, team.entity.id, day.project?.afas_project_id)
                );
                this.removeSingleUserPlanningFromProjectDay(userProjectDay, planning);
            } else {
                this.confirmDialogService.confirm({
                    title: 'Meer keren ingepland',
                    text: `De persoon is op deze dag en dit project meerdere keren ingepland en kan daarom alleen via de transport of weekplanning aangepast worden.`,
                    confirm: 'Sluiten',
                });
                return;
            }
        } else {
            const planning = this.getOrCreateTeamPlanning(
                this.projectDayTeamMap.get(this.makeKey(day.day, team.entity.id, day.project?.afas_project_id)),
                team,
                resolvedDay
            );
            if (!planning) {
                return;
            }
            const begindate = new Date(Utils.mainPlanning(planning).begindate);
            const enddate = new Date(Utils.mainPlanning(planning).enddate);
            const performerId = this.rowPerformerId(rowKey);

            if (this.userHasPlanningOnDay(day.day, user.id, day.project?.afas_project_id, rowKey, begindate, enddate)) {
                this.showAlreadyPlannedOnDayDialog('De persoon');
                return;
            }

            this.addUserPlanningToPlanningTeam(planning, user, begindate, enddate, resolvedDay, rowKey, performerId);
        }
        this.refreshPlanningView();
    }

    addUserPlanningProject(
        event,
        day: {
            day: Date;
            project?: Project;
        },
        userProjectDay: ProjectDayUserState | undefined,
        user: User,
        doNotAddEntity = false,
        performerId?: number,
        rowKey?: string
    ) {
        event.preventDefault();
        if (this.readonly) {
            return;
        }

        if (!day.project?.afas_project_id) {
            this.confirmDialogService.confirm({
                title: 'Onbekend project',
                text: `Voor deze planning is nog geen juist Afas project gekozen.`,
                confirm: 'Sluiten',
            });
            return;
        }

        const removableUserProjectDay =
            userProjectDay ?? this.getProjectDayEntry(day.day, user.id, day.project?.afas_project_id, rowKey);

        if (removableUserProjectDay?.type === 'planning') {
            if (removableUserProjectDay.userPlanning?.length + removableUserProjectDay.planningHas?.length === 1) {
                const project = this.planningList.find(
                    p =>
                        p.id ===
                        (removableUserProjectDay?.userPlanning[0]?.planning_id ??
                            removableUserProjectDay?.planningHas[0]?.planning_id)
                );
                this.planningDetailDialogService.openPlanning(project);
            } else {
                this.confirmDialogService.confirm({
                    title: 'Meerdere keren ingepland',
                    text: `De persoon is op deze dag en dit project meerdere keren ingepland en kan daarom
                    alleen via de transport of weekplanning aangepast worden.`,
                    confirm: 'Sluiten',
                });
            }
        } else {
            if (removableUserProjectDay) {
                if (removableUserProjectDay.userPlanning?.length + removableUserProjectDay.planningHas?.length > 1) {
                    this.confirmDialogService.confirm({
                        title: 'Meer keren ingepland',
                        text: `De persoon is op deze dag en dit project meerdere keren ingepland en kan daarom alleen via de transport of weekplanning aangepast worden.`,
                        confirm: 'Sluiten',
                    });
                    return;
                }

                if (removableUserProjectDay.userPlanning?.length === 1) {
                    this.removeSingleUserPlanningFromProjectDay(removableUserProjectDay);
                }
                if (removableUserProjectDay.planningHas?.length === 1) {
                    if (removableUserProjectDay.planningHas[0].id) {
                        removableUserProjectDay.planningHas[0].driver_user_id = null;
                        if (!this.planningHasToSave.includes(removableUserProjectDay.planningHas[0])) {
                            this.planningHasToSave.push(removableUserProjectDay.planningHas[0]);
                        }
                    } else {
                        if (this.planningHasToSave.includes(removableUserProjectDay.planningHas[0])) {
                            this.planningHasToSave.splice(
                                this.planningHasToSave.indexOf(removableUserProjectDay.planningHas[0]),
                                1
                            );
                        }
                    }
                }

                this.refreshPlanningView();
            } else {
                const add = () => {
                    const driverFrom = this.entities.find(e => e.driver_user_id === user.id);

                    // Driver entities and plain user plannings now share the same row-aware lookup,
                    // so assigning/removing a driver keeps working even with multiple rows per project.
                    let materialPlanningDriver =
                        driverFrom &&
                        this.getProjectDayMaterialEntry(
                            day.day,
                            driverFrom.id,
                            day.project?.afas_project_id,
                            rowKey,
                            null,
                            performerId
                        );
                    const fromDate = new Date(day.day);
                    const toDate = new Date(day.day);
                    this.setDefaultTimes(fromDate, toDate, Settings.DEFAULT_DURATION_OTHER);

                    // False when the user is already planned at the material planning's time.
                    const assignDriver = (planningHas: PlanningHasEntity) => {
                        if (
                            this.userHasPlanningOnDay(
                                day.day,
                                user.id,
                                day.project?.afas_project_id,
                                rowKey,
                                new Date(planningHas.begindate),
                                new Date(planningHas.enddate)
                            )
                        ) {
                            this.showAlreadyPlannedOnDayDialog('De persoon');
                            return false;
                        }
                        this.assignDriverToMaterialPlanning(user, planningHas, performerId);
                        return true;
                    };
                    if (driverFrom && materialPlanningDriver?.length && !doNotAddEntity) {
                        if (!assignDriver(materialPlanningDriver[0])) {
                            return;
                        }
                    }
                    if (driverFrom && !materialPlanningDriver?.length && !doNotAddEntity) {
                        if (this.addEntityPlanning(event, day, driverFrom, null, true, rowKey, performerId)) {
                            materialPlanningDriver = this.getProjectDayMaterialEntry(
                                day.day,
                                driverFrom.id,
                                day.project?.afas_project_id,
                                rowKey,
                                null,
                                performerId
                            );
                            if (!materialPlanningDriver?.length) {
                                doNotAddEntity = true;
                            } else if (!assignDriver(materialPlanningDriver[0])) {
                                return;
                            }
                        } else {
                            doNotAddEntity = true;
                        }
                    }
                    if (!driverFrom || doNotAddEntity) {
                        // A project row can inherit times from the linked asphalt planning,
                        // but only when the selected row represents the configured performer.
                        const selectedProjectDay =
                            (rowKey
                                ? this.projectDaysList?.find(projectDay => projectDay.rowKey === rowKey)
                                : undefined) ??
                            this.getProjectDayForPerformer(day.project?.afas_project_id, performerId, true);
                        const linkedPlanning = this.getLinkedPlanningForProjectDay(day.day, selectedProjectDay);
                        if (linkedPlanning) {
                            // Matching performer rows inherit the asphalt-planning times,
                            // but only when that planning is also a day-planning or night-planning in this view.
                            fromDate.setTime(new Date(Utils.mainPlanning(linkedPlanning).begindate).getTime());
                            toDate.setTime(new Date(Utils.mainPlanning(linkedPlanning).enddate).getTime());
                        }

                        if (
                            this.userHasPlanningOnDay(
                                day.day,
                                user.id,
                                day.project?.afas_project_id,
                                rowKey,
                                fromDate,
                                toDate
                            )
                        ) {
                            this.showAlreadyPlannedOnDayDialog('De persoon');
                            return;
                        }

                        const userPlanning = new UserPlanning();
                        userPlanning.afas_project_id = day.project?.afas_project_id;
                        userPlanning.user_id = user.id!;
                        userPlanning.begindate = fromDate;
                        userPlanning.enddate = toDate;
                        const shouldLinkToPlanning =
                            !!linkedPlanning?.id &&
                            selectedProjectDay ===
                                this.projectDaysList.find(
                                    projectDay => projectDay.projectId === selectedProjectDay?.projectId
                                );
                        if (shouldLinkToPlanning) {
                            userPlanning.planning_id = linkedPlanning.id;
                            userPlanning.planning = linkedPlanning;
                        }
                        if (performerId) {
                            userPlanning.performer_id = performerId;
                        }

                        this.storeUserPlanningOnProjectDay(
                            userPlanning,
                            shouldLinkToPlanning ? linkedPlanning : undefined
                        );
                    }

                    this.refreshPlanningView();
                };

                const leave = this.leaveDayUserMap.get(this.makeKey(day.day, user.id));
                if (leave) {
                    this.confirmDialogService
                        .confirm({
                            title: 'Let op: medewerker heeft verlof!',
                            text: `Deze medewerker heeft verlof op deze dag, wilt u deze persoon toch inplannen?`,
                            confirm: 'Toch inplannen',
                            cancel: 'Annuleren',
                        })
                        .then(() => add());
                } else {
                    add();
                }
            }
        }
    }

    addressSelectedForProject(address: FormattedAddress | null, projectDays: ProjectDay): void {
        if (!address?.addressLine) {
            return;
        }

        const numberIndex = address.addressLine.search(/\d/);
        const street = address.addressLine.slice(0, numberIndex < 5 ? address.addressLine.length : numberIndex).trim();
        const houseNumber = address.addressLine.replace(street, '');
        projectDays.form.controls.location_postcode.setValue(address.postalCode);
        projectDays.form.controls.location_housenumber.setValue(houseNumber);
        projectDays.form.controls.location_straat.setValue(street);
        projectDays.form.controls.location_place.setValue(address.locality);
    }

    undo() {
        setTimeout(() => {
            this.rollingBack = true;
        });
        setTimeout(() => {
            this.projectDayPerformerChangesToSave.forEach(change =>
                this.applyPerformerChange(change, change.previousPerformerId)
            );
            // Rollback any queued user planning changes to their previous values.
            this.userPlanningsToSave.forEach(userPlanning => delete userPlanning.deleted_at);
            this.userWeekCount.clear();
            this.userPlanningsToSave = [];
            this.planningHasToSave = [];
            this.planningsToSave = [];
            this.leavesToSave = [];
            this.projectDayPerformerChangesToSave = [];
            this.formDirty = false;
            // Rebuild the columns exactly like on page load (incl. its initial column filter).
            this.projectDaysList = [];
            this.initialProjectDaysFiltered = false;
            this.syncLeaveDayUserMap();
            this.parseData(
                this.planningList,
                this.projects,
                this.planningHass,
                this.userPlannings,
                this.usersMap,
                this.entitiesMap
            );
            this.rollingBack = false;
        }, 50);
    }

    save() {
        if (this.readonly) {
            return;
        }

        // Apply queued performer changes to the user plannings and collect them for saving.
        this.projectDayPerformerChangesToSave.forEach(change => {
            this.applyPerformerChange(change, change.nextPerformerId);
            change.userPlannings.forEach(userPlanning => {
                if (!this.userPlanningsToSave.some(existing => sameEntityOrSavedId(existing, userPlanning))) {
                    this.userPlanningsToSave.push(userPlanning);
                }
            });
            change.planningHass.forEach(planningHas => {
                if (!this.planningHasToSave.some(existing => sameEntityOrSavedId(existing, planningHas))) {
                    this.planningHasToSave.push(planningHas);
                }
            });
        });
        this.normalizeUserPlanningsToSave();

        this.saving = true;
        const saveCalls = [];
        // Save deletions before creates/updates from the same batch.
        // The backend duplicate check looks at the database state while it processes the array,
        // so a delete+re-add for the same slot can be rejected as "duplicate" if the create is handled first.
        const deletesFirst = (left: { deleted_at?: Date; id?: number }, right: { deleted_at?: Date; id?: number }) =>
            Number(!!right.deleted_at) - Number(!!left.deleted_at) || Number(!!right.id) - Number(!!left.id);
        let userPlanningsToSave = [...this.userPlanningsToSave].sort(deletesFirst);
        // Same idea for driver/material assignments: remove the old record first, then write the replacement.
        const planningHasToSave = [...this.planningHasToSave].sort(deletesFirst);

        userPlanningsToSave = userPlanningsToSave.filter(
            userPlanning =>
                userPlanning.id || userPlanning.deleted_at || !this.findActiveExistingUserPlanning(userPlanning)
        );

        this.leavesToSave.forEach(leave => {
            saveCalls.push(
                this.leaveService.saveLeave({ ...leave, users: leave.users?.map(u => u.id) }).pipe(
                    tap(p => {
                        leave.id = p.data.id;
                        leave.updated_at = p.data.updated_at;
                    })
                )
            );
        });
        this.leavesToSave = [];

        this.planningsToSave.forEach(planning => {
            const pl = { ...planning };
            delete pl.user_planning;
            saveCalls.push(
                from(this.planningService.saveOld(pl)).pipe(
                    tap(p => {
                        planning.id = p.id;
                        // Stays visible until the websocket delivers the saved planning
                        this.planningList.push(planning);
                        planning.user_planning
                            .filter(up => !up.id)
                            .forEach(up => {
                                up.planning_id = planning.id;
                                up.planning = null;
                            });
                    })
                )
            );
        });
        userPlanningsToSave
            .filter(up => !!up.planning_id)
            .forEach(up => {
                up.planning = null;
            });
        saveCalls.push(
            this.userPlanningService.saveUserPlanningProject(userPlanningsToSave, planningHasToSave).pipe(
                tap(response => {
                    const [savedUserPlannings, savedPlanningHas] = response.data ?? [[], []];
                    // Keep working with the original in-memory objects so the rest of this method
                    // and the next websocket refresh see the newly assigned ids instead of a "new" item twice.
                    userPlanningsToSave.forEach((userPlanning, index) => {
                        const savedUserPlanning = savedUserPlannings[index];
                        if (!userPlanning.deleted_at && savedUserPlanning?.id) {
                            delete userPlanning.deleted_at;
                            Object.assign(userPlanning, savedUserPlanning);
                        }
                    });
                    planningHasToSave.forEach((planningHas, index) => {
                        const savedPlanningHasItem = savedPlanningHas[index];
                        if (!planningHas.deleted_at && savedPlanningHasItem?.id) {
                            delete planningHas.deleted_at;
                            Object.assign(planningHas, savedPlanningHasItem);
                            if (!this.planningHass.some(existing => sameEntityOrSavedId(existing, planningHas))) {
                                this.planningHass.push(planningHas);
                            }
                        }
                    });

                    userPlanningsToSave.forEach(up => {
                        if (up.deleted_at) {
                            const idx = this.userPlannings.findIndex(existingUserPlanning =>
                                sameEntityOrSavedId(existingUserPlanning, up)
                            );
                            if (idx !== -1) {
                                this.userPlannings.splice(idx, 1);
                            }
                        } else if (
                            !this.userPlannings.some(existingUserPlanning =>
                                sameEntityOrSavedId(existingUserPlanning, up)
                            )
                        ) {
                            this.userPlannings.push(up);
                        }
                    });
                    this.userPlanningsToSave = [];
                    this.projectDayPerformerChangesToSave.forEach(change => {
                        const projectDays = this.projectDaysList?.find(
                            projectDay => projectDay.rowKey === change.rowKey
                        );
                        if (projectDays) {
                            projectDays.syncedPerformerId = projectDays.form.controls.performer_id.value ?? undefined;
                        }
                    });
                    this.projectDayPerformerChangesToSave = [];
                    this.planningHasToSave = [];
                    this.mergeDuplicatePerformerProjectRows();
                    this.refreshPlanningView();
                })
            )
        );

        concat(...saveCalls).subscribe(
            () => {
                this.planningsToSave = [];
                this.saving = false;
            },
            error => {
                console.error(error);
                this.saving = false;
            }
        );

        this.projectDaysList.forEach(projectDays => {
            // Only save project info from the base row — not from variant rows that share the same project object
            const isBaseRow = this.projectDaysList.find(pd => pd.projectId === projectDays.projectId) === projectDays;
            if (projectDays.form.dirty && projectDays.project && isBaseRow) {
                const { performer_id, ...projectFields } = projectDays.form.value;
                Object.assign(projectDays.project, projectFields);
                this.projectService.saveProject(projectDays.project).subscribe(
                    p => {
                        projectDays.project.updated_at = p.data.updated_at;
                        projectDays.form.markAsPristine();
                        this.saving = false;
                    },
                    () => {
                        this.saving = false;
                    }
                );
            } else if (projectDays.form.dirty) {
                // Performer changes live on user_planning, so rows without project field edits do not write the project.
                projectDays.form.markAsPristine();
            }
        });
    }

    /** Appends only non-matching items from `right` into `left`. */
    private mergeUniqueItems<T>(left: T[] = [], right: T[] = [], matches: (left: T, right: T) => boolean): T[] {
        const result = [...left];

        right.forEach(item => {
            if (!result.some(existingItem => matches(existingItem, item))) {
                result.push(item);
            }
        });

        return result;
    }

    /** Collects the user_planning rows that currently belong to one visible project row. */
    private getProjectDayRowUserPlannings(rowKey: string): UserPlanning[] {
        return Array.from(this.projectDayUserMap.entries())
            .filter(([key]) => key.endsWith(`-${rowKey}`))
            .flatMap(([, projectDayUser]) => projectDayUser.userPlanning ?? []);
    }

    /** Collects the planning_has rows that currently belong to one visible project row. */
    private getProjectDayRowPlanningHass(rowKey: string): PlanningHasEntity[] {
        const rowPlanningHass = Array.from(this.projectDayUserMap.entries())
            .filter(([key]) => key.endsWith(`-${rowKey}`))
            .flatMap(([, projectDayUser]) => projectDayUser.planningHas ?? []);
        const rowMaterialPlanningHass = Array.from(this.projectDayMaterialMap.entries())
            .filter(([key]) => key.includes(`-${rowKey}`))
            .flatMap(([, projectDayMaterial]) => projectDayMaterial);

        return this.mergeUniqueItems(rowPlanningHass, rowMaterialPlanningHass, sameEntityOrSavedId);
    }

    /** Moves the records of a row-level performer change to the given performer (and so to its row). */
    private applyPerformerChange(change: ProjectDayPerformerChange, performerId: number | null): void {
        [...change.userPlannings, ...change.planningHass].forEach(
            item => (item.performer_id = performerId ?? undefined)
        );
    }

    private clearMaps() {
        this.teardownPlanningPointerOverListener();
        this.userRowFingerprints.clear();
        this.leaveDayUserMap.clear();
        this.entityHiringNames.clear();
        this.projectDaysList = [];
        this.initialProjectDaysFiltered = false;
        this.projectDayUserMap.clear();
        this.projectDayTeamMap.clear();
        this.userDayConflictMap.clear();
        this.userWeekCount.clear();
        this.entityWeekUsedDays.clear();
    }

    next() {
        const lastWeek = this.week >= Utils.getWeekNumber(new Date(this.year, 11, 28));
        this.goToWeek(lastWeek ? 1 : this.week + 1, lastWeek ? this.year + 1 : this.year);
    }

    prev() {
        const firstWeek = this.week <= 1;
        this.goToWeek(
            firstWeek ? Utils.getWeekNumber(new Date(this.year - 1, 11, 28)) : this.week - 1,
            firstWeek ? this.year - 1 : this.year
        );
    }

    private goToWeek(week: number, year: number) {
        this.openChangesBackActionCheck().then(result => {
            if (result) {
                this.dataLoadInProgress = true;
                this.clearMaps();
                this.router.navigate(['personeelsplanning', week, year]);
            }
        });
    }

    /**
     * Move this to a separate function to avoid creating additional subscriptions to the user and leave streams.
     */
    private syncLeaveDayUserMap(): void {
        this.leaveDayUserMap.clear();

        if (!Array.isArray(this.allUsers) || !Array.isArray(this.leaveList)) {
            return;
        }

        this.leaveList.forEach(leave => this.employeeService.leaveToMap(this.leaveDayUserMap, leave, this.allUsers));
    }

    getData() {
        this.dataLoadInProgress = true;
        const requestFromDate = new Date(this.fromDate);
        const requestToDate = new Date(this.toDate);

        // --- Observable definitions ---
        const planning$ = this.planningService.getFilteredList(requestFromDate, requestToDate);
        const project$ = this.projectService.getFilteredList(requestFromDate, requestToDate);
        const planningHas$ = this.planningHasService.getFilteredList(requestFromDate, requestToDate);
        const userPlanning$ = this.userPlanningService.getFilteredList(requestFromDate, requestToDate);

        // AFAS absence data is optional: skipped when AFAS is disabled or in read-only mode.
        const skipAfas = environment.disableAfas || this.readonly;
        const leaves$ = skipAfas ? of(null) : this.afasService.getLeaves(requestFromDate, requestToDate);
        const illness$ = skipAfas ? of(null) : this.afasService.getIllness(requestFromDate, requestToDate);

        const planningUsers$ = this.userService
            .getList(false, true)
            .pipe(
                map(users =>
                    users.sort((a, b) =>
                        (a.lastname ?? '').localeCompare(b.lastname ?? '', 'nl', { sensitivity: 'base', numeric: true })
                    )
                )
            );
        const rolesMap$ = this.authorisationService.getRolesMap();
        const entities$ = this.entitiesService.getList(this.fromDate, this.toDate);

        const leave$ = this.leaveService.getFilteredList(this.fromDate, this.toDate);

        this.blockedMap = null;
        this.periodSubscriptions?.unsubscribe();
        this.periodSubscriptions = new Subscription();

        // --- Subscription 1: autocomplete for hiring names ---
        this.periodSubscriptions.add(
            this.fcAddHiring.valueChanges.pipe(debounceTime(200)).subscribe(value => {
                this.periodSubscriptions.add(
                    this.planningService.getHiringNames(this.addingHiringForEntityId).subscribe(names => {
                        const searchValue = (value ?? '').toLowerCase();
                        let newHirings = names.data.filter(name => name?.toLowerCase().includes(searchValue));
                        if (value?.length) {
                            newHirings.push(value);
                        }
                        const currentNames = this.entityHiringNames.get(this.addingHiringForEntityId) ?? [];
                        newHirings = newHirings
                            .filter(newHiring => currentNames.indexOf(newHiring) === -1 && newHiring?.length > 0)
                            .slice(-5);
                        this.hiringAutocompleteOptions = newHirings;
                    })
                );
            })
        );

        // --- Subscription 2: leave data ---
        // Kept separate from the main combineLatest so that leave updates don't trigger a full planning re-parse.
        this.periodSubscriptions.add(
            leave$.subscribe(leaves => {
                this.leaveData = leaves;
                this.leaveList = [...leaves];
                this.syncLeaveDayUserMap();
                this.leaveDayUserMap = new Map(this.leaveDayUserMap);
            })
        );

        // --- Subscription 3: main planning data ---
        // Waits for all core data sources before processing. On each emission
        // (e.g. websocket push) the full planning grid is rebuilt.
        const handleData = ([planningHas, userPlanning, planningList, projects, planningUsers, rolesMap, entities]: [
            PlanningHasEntity[],
            UserPlanning[],
            Planning[],
            Project[],
            User[],
            Map<string, Role>,
            Entity[],
        ]) => {
            this.usersMap = new Map(planningUsers.filter(user => user.id).map(user => [user.id!, user]));
            const entitiesMap = new Map(entities.filter(entity => entity.id).map(entity => [entity.id, entity]));
            this.entitiesMap = entitiesMap;
            this.allUsers = planningUsers;
            this.projectManagers = planningUsers.filter(user =>
                user.roles?.some(role => role.key === this.environment.roles.Projectmanager)
            );
            // Track Line: the weekplanning also allows (Groep)-role users as voorman.
            this.executors = planningUsers.filter(
                user =>
                    user.roles?.some(role => role.key === this.environment.executor) ||
                    (this.environment.platform === 'trackline' && planningList.some(p => p.performer === user.id))
            );
            this.syncLeaveDayUserMap();

            // Build users, uniqueRoles and teams. In asfaltploegen-mode
            // users and roles are empty — only teams (ploegen) are relevant.
            const plannableUsers = planningUsers.filter(user =>
                AuthorisationService.hasFunctionality(Functionalities.Plannable, user)
            );

            if (this.router.routerState.snapshot.url.substring(0, 14) === '/asfaltploegen') {
                this.asfaltploegenModus = true;
            }

            const users = this.asfaltploegenModus ? [] : plannableUsers;

            this.setRoles(rolesMap, users);

            // Only entities that are a team-type AND have >1 known members are shown.
            this.teams = this.buildTeams(entities, planningUsers);

            this.users = users;
            const selectedRoleKeys = this.fcRoleKeys.value ?? [];
            const validRoleKeys = selectedRoleKeys.filter(roleKey =>
                [...(this.uniqueRoles ?? []), ...(this.groupRoles ?? [])].some(role => role.key === roleKey)
            );
            if (selectedRoleKeys.length !== validRoleKeys.length) {
                this.fcRoleKeys.setValue(validRoleKeys);
            }
            this.smallMaterial = entities.filter(e => !!e.entitytypes.find(e => e.visible_small) && !!e.use_once);

            this.hireEntities = entities.filter(
                e => !!e.entitytypes.find(e => e.visible_small) && !e.use_once && HIRE_ENTITY_IDS.includes(e.id)
            );
            this.entities = entities;

            // Once blockedMap is initialised, absence data is loaded once
            // and merged into the view via a nested subscription.
            if (!this.blockedMap) {
                this.blockedMap = new Map();
                this.periodSubscriptions.add(
                    combineLatest([leaves$, illness$]).subscribe(([leaves, illness]) => {
                        const leavesAndIlls = [
                            ...(illness?.data.map(ill => {
                                return {
                                    startDate: ill.startDate,
                                    endDate: ill.endDate,
                                    description: ill.absenceTypeDesc,
                                    employeeId: ill.employeeId,
                                    icon: 'fa-face-thermometer',
                                };
                            }) ?? []),
                            ...(leaves?.data.map(leave => {
                                return {
                                    startDate: leave.startDate,
                                    endDate: leave.endDate,
                                    description: leave.leaveDescr,
                                    employeeId: leave.employeeId,
                                    icon: 'fa-user-slash',
                                };
                            }) ?? []),
                        ];

                        const loopDate = new Date(this.fromDate);
                        while (loopDate.getTime() < this.toDate.getTime()) {
                            const startOfDay = Utils.setTime(new Date(loopDate), 0, 0);
                            const endOfDay = Utils.setTime(new Date(loopDate), 23, 59);
                            leavesAndIlls.forEach(leaveOrIll => {
                                const user = this.users.find(u => u.afas_employee_id === leaveOrIll.employeeId);
                                if (
                                    Utils.newDate(leaveOrIll.startDate) <= endOfDay &&
                                    (leaveOrIll.endDate === null || Utils.newDate(leaveOrIll.endDate) >= startOfDay) &&
                                    user
                                ) {
                                    this.blockedMap.set(this.makeKey(startOfDay, user.id), {
                                        description: leaveOrIll.description,
                                        icon: leaveOrIll.icon,
                                        startDate: leaveOrIll.startDate,
                                        endDate: leaveOrIll.endDate,
                                    });
                                }
                            });
                            loopDate.setDate(loopDate.getDate() + 1);
                        }

                        this.blockedMap = new Map(this.blockedMap);
                        this.refreshPlanningView();
                    })
                );
            }
            this.projects = projects;
            this.planningList = planningList;
            this.planningHass = planningHas;
            this.userPlannings = userPlanning;
            // Websocket data resets performer_id on the server records; re-apply the queued row changes.
            this.projectDayPerformerChangesToSave.forEach(change => {
                change.userPlannings = change.userPlannings.map(
                    up => userPlanning.find(s => up.id && s.id === up.id) ?? up
                );
                change.planningHass = change.planningHass.map(
                    ph => planningHas.find(s => ph.id && s.id === ph.id) ?? ph
                );
                this.applyPerformerChange(change, change.nextPerformerId);
            });
            this.parseData(planningList, projects, planningHas, userPlanning, this.usersMap, entitiesMap);

            this.dataLoadInProgress = false;
            requestAnimationFrame(() => this.setupPlanningPointerOverListener());
            requestAnimationFrame(() => document.body.appendChild(document.createElement('readyforpuppeteer')));
        };

        // Coalesce websocket bursts once, after all latest values have been combined.
        this.periodSubscriptions.add(
            combineLatest([planningHas$, userPlanning$, planning$, project$, planningUsers$, rolesMap$, entities$])
                .pipe(debounceTime(100))
                .subscribe(handleData)
        );
    }

    showInfoDialog() {
        const planningList = this.planningList;
        const planningHas = this.planningHass.filter(
            p =>
                p.driver_user_id === this.contextUser?.id &&
                Utils.formatDate(p.begindate) === Utils.formatDate(this.contextDay)
        );
        const userPlanning = this.userPlannings.filter(
            p =>
                p.user_id === this.contextUser?.id &&
                Utils.formatDate(p.begindate) === Utils.formatDate(this.contextDay)
        );
        const leave = this.leaveDayUserMap.get(this.makeKey(this.contextDay, this.contextUser?.id));
        const afasLeave = this.blockedMap.get(this.makeKey(this.contextDay, this.contextUser?.id));
        this.dialog.open(EmployeeDayInfoDialogComponent, {
            minWidth: '320px',
            panelClass: 'comment-edit-dialog',
            data: {
                planningHas,
                userPlanning,
                leaves: [leave].filter(Boolean),
                afasLeave,
                projects: this.projects,
                planningList,
                user: this.contextUser,
                day: this.contextDay,
            },
        });
    }

    private parseData(
        planningList: Planning[],
        projects: Project[],
        planningHass: PlanningHasEntity[],
        userPlannings: UserPlanning[],
        usersMap: Map<number, User>,
        entitiesMap: Map<number, Entity>
    ) {
        userPlannings = (userPlannings ?? []).filter(userPlanning => !userPlanning.deleted_at);

        this.projectDaysList ??= [];

        planningList.forEach(planning => {
            planning.performer_user = usersMap.get(planning.performer);
            planning.projectmanager_user = usersMap.get(planning.projectmanager);

            Utils.planningAllEntities(planning).forEach(plnEntity => {
                plnEntity.entity = entitiesMap.get(plnEntity.entity_id);
            });
        });

        // Build projectDaysList rows first so userPlannings can find the right rowKey
        this.uniqueProjectsIds = [
            ...new Set([
                ...projects.map(project => project.afas_project_id),
                ...planningList.map(planning => planning.afas_project_id ?? planning.worknumber),
                ...planningHass.map(planningHas => this.resolveProjectId(planningHas, planningList)),
                ...userPlannings.map(userPlanning => this.resolveProjectId(userPlanning, planningList)),
            ]),
        ]
            .filter((projectId): projectId is string => !!projectId)
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

        this.uniqueProjectsIds.forEach(projectId => {
            const project = projects.find(p => p.afas_project_id === projectId);
            if (project) {
                const projectUserPlannings = userPlannings.filter(
                    up => this.resolveProjectId(up, planningList) === projectId && !up.deleted_at
                );
                const projectPlanningHas = planningHass.filter(
                    planningHas =>
                        this.resolveProjectId(planningHas, planningList) === projectId && !planningHas.deleted_at
                );
                const projectPlanningHasWithDriver = projectPlanningHas.filter(
                    planningHas => !!planningHas.driver_user_id
                );
                const hasPersonnelPlanning =
                    projectUserPlannings.some(userPlanning => !!userPlanning.user_id) ||
                    projectPlanningHasWithDriver.length > 0 ||
                    planningList.some(
                        planning =>
                            (planning.afas_project_id ?? planning.worknumber) === projectId &&
                            planning.user_planning?.some(
                                userPlanning => !userPlanning.deleted_at && userPlanning.user_id
                            )
                    );
                const baseProjectDay = this.projectDaysList.find(p => p.projectId === projectId);

                // Before the initial filter every project needs a row so it can be checked for planning data.
                // Afterwards an absent row is recreated only when websocket data contains personnel planning for it.
                if (!baseProjectDay && (!this.initialProjectDaysFiltered || hasPersonnelPlanning)) {
                    this.projectDaysList.push(this.createProjectDays(projectId, projects, planningList));
                } else if (baseProjectDay) {
                    if (!baseProjectDay.form.dirty) {
                        const rowFields = this.rowProjectFields(projectId, project);
                        baseProjectDay.form.patchValue({ ...project, ...rowFields }, { emitEvent: false });
                        baseProjectDay.syncedPerformerId = rowFields.performer_id;
                    }
                }

                // Create extra rows for each distinct performer_id found in user_plannings for this project
                // so existing data can be mapped back into separate visible columns.
                const projectPlanningHasPerformerIds = projectPlanningHasWithDriver
                    .map(planningHas => this.resolvePlanningHasPerformerId(planningHas, projectId, userPlannings))
                    .filter((performerId): performerId is number => performerId != null);
                const extraPerformerIds = [
                    ...new Set([
                        ...projectUserPlannings
                            .map(up => this.itemPerformerId(up))
                            .filter((performerId): performerId is number => performerId != null),
                        ...projectPlanningHasPerformerIds,
                    ]),
                ];
                extraPerformerIds.forEach(performerId => {
                    const matchingRows = this.projectDaysList.filter(
                        p =>
                            p.project?.afas_project_id === projectId &&
                            p.form.controls.performer_id.value === performerId
                    );
                    if (!matchingRows.length) {
                        const variantRow = this.createProjectDays(projectId, projects, planningList);
                        variantRow.form.patchValue(
                            { ...project, ...this.rowProjectFields(projectId, project, performerId) },
                            { emitEvent: false }
                        );
                        variantRow.syncedPerformerId = performerId;
                        const projectRows = this.projectDaysList.filter(
                            projectDay => projectDay.projectId === projectId
                        );
                        const insertAfterRow = projectRows[projectRows.length - 1];
                        const insertIndex = insertAfterRow
                            ? this.projectDaysList.indexOf(insertAfterRow) + 1
                            : this.projectDaysList.length;

                        this.projectDaysList.splice(insertIndex, 0, variantRow);
                    }
                });
            } else {
                // Realtime data can emit synchronously, so take(1) avoids self-unsubscribing before assignment.
                this.projectService.getSingle(projectId).pipe(take(1)).subscribe();
            }
        });

        userPlannings.forEach(userPlanning => {
            // Websocket data refreshes queued copies of the same record.
            const queued = this.userPlanningsToSave.find(up => up.id && up.id === userPlanning.id);
            if (queued && queued !== userPlanning) {
                delete userPlanning.deleted_at;
                Object.assign(queued, userPlanning);
            }

            const planning = planningList.find(p => p.id === userPlanning.planning_id);
            if (planning && !planning.user_planning.find(up => up.id === userPlanning.id)) {
                planning.user_planning.push(userPlanning);
            }
        });

        this.planningList?.forEach(planning => {
            planning.user_planning = planning.user_planning.filter(up => !!userPlannings.find(ups => ups.id === up.id));
        });

        this.refreshPlanningView();
        this.initialProjectDaysFiltered = true;
    }

    private generateCounts() {
        if (!this.users?.length || !this.leaveDays?.length) {
            return;
        }

        this.userDayHasNightMap.clear();
        this.userDayConflictMap.clear();
        const userDayPlanningWindows = new Map<string, Array<{ start: Date; end: Date; isNight: boolean }>>();
        const addPlanningWindow = (begindate: Date, enddate: Date, userId: number): void => {
            const userDayKey = this.makeKey(Utils.newDate(begindate), userId);
            const planningWindow = {
                start: new Date(begindate),
                end: new Date(enddate),
                isNight: Utils.isNight(begindate, enddate),
            };

            if (planningWindow.isNight) {
                this.userDayHasNightMap.set(userDayKey, true);
            }

            const planningWindows = userDayPlanningWindows.get(userDayKey) ?? [];
            planningWindows.push(planningWindow);
            userDayPlanningWindows.set(userDayKey, planningWindows);
        };

        this.projectDayUserMap.forEach(projectDayUser => {
            projectDayUser.userPlanning?.forEach(({ begindate, enddate, user_id, deleted_at }) => {
                if (!deleted_at) {
                    addPlanningWindow(begindate, enddate, user_id);
                }
            });

            projectDayUser.planningHas?.forEach(({ begindate, enddate, driver_user_id, deleted_at }) => {
                if (!deleted_at) {
                    addPlanningWindow(begindate, enddate, driver_user_id);
                }
            });
        });

        userDayPlanningWindows.forEach((planningWindows, userDayKey) => {
            if (planningWindows.length > 1) {
                this.userDayConflictMap.set(userDayKey, true);
            }
        });
    }

    /** Derives every lookup map from the server data plus the queued changes. */
    private rebuildView(): void {
        // Queued copies win over the server version of the same record; new records have no id yet.
        const latest = <T extends { id?: number }>(server: T[] = [], queued: T[] = []) => [
            ...new Map([...server, ...queued].map(item => [item.id ?? item, item] as [unknown, T])).values(),
        ];
        this.projectDayUserMap.clear();
        this.dayUserMap.clear();
        this.projectDayMaterialMap.clear();
        this.projectDayTeamMap.clear();
        this.entityWeekUsedDays.clear();
        this.userDatesMap.clear();
        const nightUserDays = new Set<string>();
        const addUserDay = (date: Date, userId: number, projectId: string, item: UserPlanning | PlanningHasEntity) => {
            const userDayKey = this.makeKey(date, userId);
            const isNight = !!Utils.isNight(item.begindate, item.enddate);
            if (!this.dayUserMap.has(userDayKey) || (isNight && !nightUserDays.has(userDayKey))) {
                this.dayUserMap.set(userDayKey, projectId);
            }
            if (isNight) {
                nightUserDays.add(userDayKey);
            }
            this.userDatesMap.set(userId, [...(this.userDatesMap.get(userId) ?? []), Utils.formatDate(date)]);
        };
        const cellAt = (key: string, type: ProjectDayUserState['type']) => {
            const cell = this.projectDayUserMap.get(key) ?? { type, userPlanning: [], planningHas: [] };
            this.projectDayUserMap.set(key, cell);
            return cell;
        };

        [...(this.planningList ?? []), ...this.planningsToSave.filter(p => !this.planningList?.includes(p))].forEach(
            planning => {
                const main = Utils.mainPlanning(planning);
                const key = this.makeKey(
                    new Date(main.begindate),
                    planning.mainPlanning?.entity_id ?? main.entity_id,
                    planning.afas_project_id ?? planning.worknumber
                );
                this.projectDayTeamMap.set(key, planning);
            }
        );

        latest(this.planningHass, this.planningHasToSave)
            .filter(planningHas => !planningHas.deleted_at && planningHas.entity_id)
            .forEach(planningHas => {
                const projectId = this.resolveProjectId(planningHas);
                const date = new Date(planningHas.begindate);
                const entity = this.entitiesMap?.get(planningHas.entity_id);
                if (!entity?.use_once && HIRE_ENTITY_IDS.includes(planningHas.entity_id)) {
                    const names = this.entityHiringNames.get(planningHas.entity_id) ?? [];
                    if (!names.includes(planningHas.name)) {
                        this.entityHiringNames.set(planningHas.entity_id, [...names, planningHas.name]);
                    }
                }

                planningHas.performer_id ??= this.resolvePlanningHasPerformerId(planningHas, projectId);

                if (planningHas.driver_user_id) {
                    const rowKey =
                        this.getProjectDayForPerformer(projectId, planningHas.performer_id, !planningHas.planning_id)
                            ?.rowKey ?? projectId;
                    cellAt(
                        this.makeKey(date, planningHas.driver_user_id, rowKey),
                        planningHas.planning_id ? 'planning' : 'project'
                    ).planningHas.push(planningHas);
                    addUserDay(date, planningHas.driver_user_id, projectId, planningHas);
                }

                // All material, because entities are coupled automatically
                const nonUseOnceName = entity?.use_once ? null : planningHas.name;
                const materialKey = this.getProjectDayMaterialMapKeys(date, planningHas.entity_id, {
                    projectId,
                    nonUseOnceName,
                    performerId: planningHas.performer_id,
                    preferLastMatchingRow: !planningHas.planning_id,
                })[0];
                if (materialKey) {
                    this.projectDayMaterialMap.set(materialKey, [
                        ...(this.projectDayMaterialMap.get(materialKey) ?? []),
                        planningHas,
                    ]);
                }

                const usedDays = this.entityWeekUsedDays.get(nonUseOnceName + planningHas.entity_id) ?? [];
                if (!usedDays.includes(Utils.formatDate(date))) {
                    this.entityWeekUsedDays.set(nonUseOnceName + planningHas.entity_id, [
                        ...usedDays,
                        Utils.formatDate(date),
                    ]);
                }
            });

        latest(this.userPlannings, this.userPlanningsToSave)
            .filter(userPlanning => !userPlanning.deleted_at)
            .forEach(userPlanning => {
                const projectId = this.resolveProjectId(userPlanning);
                const date = new Date(userPlanning.begindate);
                // performer_id determines the row; without it the planning belongs to the base project row.
                const performerId = this.itemPerformerId(userPlanning);
                const rowKey =
                    (performerId != null
                        ? this.getProjectDayForPerformer(projectId, performerId, !userPlanning.planning_id)
                        : this.projectDaysList?.find(pd => pd.projectId === projectId)
                    )?.rowKey ?? projectId;
                cellAt(
                    this.makeKey(date, userPlanning.user_id, rowKey),
                    userPlanning.planning_id || userPlanning.planning ? 'userPlanning' : 'project'
                ).userPlanning.push(userPlanning);
                addUserDay(date, userPlanning.user_id, projectId, userPlanning);
            });

        // Only planned days count; leave and absence do not.
        const currentDates = this.leaveDays.map(day => day.dayKey);
        this.userWeekCount.clear();
        this.userDatesMap.forEach((dates, userId) => {
            this.userWeekCount.set(userId, new Set(dates.filter(date => currentDates.includes(date))).size);
        });
        this.generateCounts();
    }

    private createProjectDays(afasProjectId: string, projects: Project[], planningList?: Planning[]): ProjectDay {
        const planning =
            planningList?.find(p => (p.afas_project_id ?? p.worknumber) === afasProjectId && !p.is_transport) ??
            planningList?.find(p => (p.afas_project_id ?? p.worknumber) === afasProjectId);
        const project = projects.find(p => p.afas_project_id === afasProjectId) ?? planning?.project;
        const rowFields = this.rowProjectFields(afasProjectId, project);
        const projectDays: ProjectDay = {
            project,
            projectId: afasProjectId,
            rowKey: `${afasProjectId}::${this.projectDayRowCounter++}`,
            syncedPerformerId: rowFields.performer_id,
            form: new FormGroup<ProjectDayFormControls>({
                location: new FormControl(rowFields.location),
                location_housenumber: new FormControl(project?.location_housenumber),
                location_place: new FormControl(project?.location_place),
                location_postcode: new FormControl(project?.location_postcode),
                location_straat: new FormControl(project?.location_straat),
                performer_id: new FormControl(rowFields.performer_id),
                projectmanager_id: new FormControl(rowFields.projectmanager_id),
            }),
            days: [] as ProjectDay['days'],
        };
        if (!project?.afas_project_id) {
            projectDays.form.disable();
        }
        this.periodSubscriptions.add(
            projectDays.form.dirty$.subscribe(dirty => {
                this.formDirty = dirty;
            })
        );
        // When performer_id changes, the projectDay may need to move to a different row (the one matching the new performer_id, or the base row if no performer_id).
        this.periodSubscriptions.add(
            projectDays.form.controls.performer_id.valueChanges.subscribe(() => {
                if (this.rollingBack) {
                    return;
                }

                const previousPerformerId = projectDays.syncedPerformerId ?? null;
                const nextPerformerId = projectDays.form.controls.performer_id.value ?? null;
                const existingChange = this.projectDayPerformerChangesToSave.find(
                    change => change.rowKey === projectDays.rowKey
                );
                const rowUserPlannings = this.getProjectDayRowUserPlannings(projectDays.rowKey);
                const rowPlanningHass = this.getProjectDayRowPlanningHass(projectDays.rowKey);
                // Variants can exist without linked plannings (for example material-only rows).
                // The row-level change is still queued so undo can restore the previous selection.
                const change: ProjectDayPerformerChange = {
                    rowKey: projectDays.rowKey,
                    previousPerformerId,
                    nextPerformerId,
                    userPlannings: rowUserPlannings.length ? rowUserPlannings : (existingChange?.userPlannings ?? []),
                    planningHass: rowPlanningHass.length ? rowPlanningHass : (existingChange?.planningHass ?? []),
                };
                this.applyPerformerChange(change, nextPerformerId);

                const changes = this.projectDayPerformerChangesToSave;
                if (previousPerformerId === nextPerformerId) {
                    this.projectDayPerformerChangesToSave = changes.filter(c => c !== existingChange);
                } else if (existingChange) {
                    changes.splice(changes.indexOf(existingChange), 1, change);
                } else {
                    changes.push(change);
                }
                this.refreshPlanningView();
            })
        );

        this.leaveDays.forEach(({ day, dayLetter, dayKey, dayLabel, cssClass }) => {
            projectDays.days.push({ day, project, dayLetter, dayKey, dayLabel, cssClass });
        });
        return projectDays;
    }

    addHiring(teamName: string) {
        const newHiring = `${teamName ?? ''}`.trim();
        if (!newHiring || !this.addingHiringForEntityId) {
            return;
        }
        const array = this.entityHiringNames.get(this.addingHiringForEntityId) ?? [];
        if (array.indexOf(newHiring) === -1) {
            array.push(newHiring);
            this.entityHiringNames.set(this.addingHiringForEntityId, array);
        }
        this.fcAddHiring.reset();
        this.addingHiringForEntityId = null;
    }

    startAddHiring(entity: Entity) {
        if (this.readonly) {
            return;
        }

        this.addingHiringForEntityId = entity.id;
        requestAnimationFrame(() => {
            this.addHiringInput.nativeElement.focus();
        });
    }

    pdf() {
        this.downloadingPdf = true;
        const filename = `Personeelsplanning-week_${this.week}-${this.year}.pdf`;
        this.subscriptions.add(
            this.planningService.generateEmployeePdf(this.week, this.year).subscribe({
                next: (data: Blob) => {
                    saveAs(data, filename);

                    this.downloadingPdf = false;
                },
                error: () => {
                    this.downloadingPdf = false;
                },
            })
        );
    }

    removeHire(hireEntityId: number, user: string) {
        if (this.entityWeekUsedDays.get(user + hireEntityId)?.length) {
            this.confirmDialogService.confirm({
                title: 'Verwijderen niet mogelijk',
                text: `Verwijderen is niet mogelijk omdat er nog inhuur ingepland is. Verwijder eerst de inhuur.`,
                confirm: 'Sluiten',
            });
        } else {
            const items = this.entityHiringNames.get(hireEntityId);
            items.splice(items.indexOf(user), 1);
            this.entityHiringNames.set(hireEntityId, items);
        }
    }

    setHourType(hourtype: RealisationHourtype) {
        this.currentHourType = hourtype;
        this.addOrRemoveLeave(this.contextUser, this.contextDay, true, true);
    }

    addOrRemoveLeave(user: User, day: Date, setHourType?: boolean, changeProject?: boolean) {
        const dateString = Utils.formatDate(day);
        const checkHourtype = this.leaveDayUserMap.get(this.makeKey(day, user.id))?.hourtype ?? this.currentHourType;
        const leaves = this.leaveData.filter(
            l => l.hourtype === checkHourtype && dateString === Utils.formatDate(l.begindate) && !l.deleted_at
        );

        let leave = leaves[0];
        if (leaves.length > 1) {
            leave = leaves.find(l => !!l.users.find(u => u.id === user.id));
        }
        if (this.currentHourType === RealisationHourtype.sleep) {
            leave = this.leaveData.find(
                l =>
                    l.hourtype === checkHourtype &&
                    dateString === Utils.formatDate(l.begindate) &&
                    !l.deleted_at &&
                    l.afas_project_id === this.contextProject?.afas_project_id
            );
        }

        if (leave && leave?.interval_week) {
            this.confirmDialogService
                .confirm({
                    title: 'Herhalend verlof',
                    text: `Herhalend verlof kan alleen via de verlofmodule worden aangepast.`,
                    confirm: 'Sluiten',
                    cancel: 'Naar verlof detail',
                })
                .catch(() => this.router.navigateByUrl(`/leave/${leave.id}`));
            return;
        }

        if (!this.leaveDayUserMap.has(this.makeKey(day, user.id)) && leave?.comment !== this.contextComment) {
            leave = null;
        }
        if (
            (this.currentHourType === RealisationHourtype.sleep &&
                leave?.afas_project_id !== this.contextProject?.afas_project_id) ||
            changeProject
        ) {
            leave = null;
        }

        const leaveUsers = () => {
            if (!leave.users) {
                leave.users = [];
            }
            let actionIsAdd = false;

            if (leave.excluded.find(e => e.user_id === user.id)) {
                leave.excluded.splice(
                    leave.excluded.findIndex(e => e.user_id === user.id),
                    1
                );
                actionIsAdd = true;
            } else {
                if (leave?.common) {
                    leave.excluded.push({ user_id: user.id, date: day });
                    this.leaveDayUserMap.delete(this.makeKey(day, user.id));
                } else {
                    if (leave.users.find(u => u.id === user.id)) {
                        leave.users.splice(
                            leave.users.findIndex(u => u.id === user.id),
                            1
                        );
                        this.leaveDayUserMap.delete(this.makeKey(day, user.id));
                        actionIsAdd = true;
                    } else {
                        leave.users.push(user);
                    }
                }
            }

            if (!this.leaveData.includes(leave)) {
                this.leaveData.push(leave);
            }
            if (!this.leavesToSave.includes(leave)) {
                this.leavesToSave.push(leave);
            }
            this.employeeService.leaveToMap(this.leaveDayUserMap, leave, this.users);
            this.refreshPlanningView();
            return actionIsAdd;
        };

        if (!leave) {
            if (!this.currentHourType) {
                return;
            }
            const dialogRef = this.dialog.open<AddLeaveNameDialogComponent, any, returnData>(
                AddLeaveNameDialogComponent,
                {
                    width: '320px',
                    panelClass: 'comment-edit-dialog',
                    data: {
                        currentHourType: this.currentHourType,
                        contextComment: this.contextComment,
                        contextProject: this.contextProject,
                        projects: this.projectDaysList.map(pd => pd.project),
                    },
                }
            );
            dialogRef.afterClosed().subscribe(data => {
                if (data) {
                    this.contextProject = data.project;
                    this.contextComment = data.comment;
                    leave = new Leave();
                    leave.hourtype = this.currentHourType;
                    if (leave.hourtype === RealisationHourtype.sleep) {
                        leave.afas_project_id = this.contextProject.afas_project_id;
                    }
                    const beginDate = new Date(day);
                    Utils.setTime(beginDate, Settings.DEFAULT_START, 0, 0);
                    const endDate = new Date(beginDate);
                    Utils.setTime(endDate, Settings.DEFAULT_START + Settings.DEFAULT_DURATION_SLEEP, 0, 0);
                    leave.begindate = beginDate;
                    leave.enddate = endDate;
                    leave.comment = data.comment;
                    leave.begintime = Utils.formatDate(beginDate, 'HH:mm');
                    leave.endtime = Utils.formatDate(endDate, 'HH:mm');
                    leaveUsers();
                }
            });
        } else {
            if (leaveUsers() && setHourType) {
                this.addOrRemoveLeave(this.contextUser, this.contextDay);
            }
        }
    }

    showProjectDetails(project: Project) {
        const executor = this.executors?.find(e => e.id === project.performer_id);
        const projectManager = this.projectManagers?.find(pm => pm.id === project.projectmanager_id);
        this.dialog.open(ProjectDetailDialogComponent, {
            width: '99vw',
            maxWidth: '400px',
            panelClass: 'project-detail-dialog',
            data: {
                project,
                executor,
                projectManager,
            },
        });
    }

    private setRoles(rolesMap: Map<string, Role>, users: User[]): void {
        const roles = Array.from(rolesMap.values());
        const hasRole = (user: User, roleKey: string) => !!user.roles?.some(role => role.key === roleKey);
        // Each unique role gets its users; a user only appears under the first matching role.
        const seenUserIds = new Set<number>();
        this.uniqueRoles = roles
            .filter(
                role =>
                    role.is_unique &&
                    role.key !== this.environment.roles.Customer &&
                    (!role.restricted || AuthorisationService.hasRole(this.environment.roles.Codalt))
            )
            .map(role => {
                const roleUsers = users.filter(user => hasRole(user, role.key) && !seenUserIds.has(user.id));
                roleUsers.forEach(user => seenUserIds.add(user.id));
                return { ...role, users: roleUsers };
            })
            .filter(role => role.users.length);
        this.groupRoles = roles.filter(
            role => GROUP_ROLE_PREFIX.test(role.name) && users.some(user => hasRole(user, role.key))
        );
    }

    /** Team-type entities with more than one known member, sorted by team type. */
    private buildTeams(entities: Entity[], planningUsers: User[]): Team[] {
        return entities
            .filter(
                e =>
                    !!e.entitytypes.find(t => t.is_team) &&
                    e.default_employees?.filter(df => this.usersMap.has(df))?.length > 1
            )
            .map(team => {
                const teamUserIds = new Set(team.default_employees?.map(employeeId => +employeeId) ?? []);
                return {
                    entityType: team.entitytypes.find(e => e.is_team),
                    entity: team,
                    users: planningUsers.filter(user => user.id && teamUserIds.has(user.id)),
                    showUsers: this.asfaltploegenModus,
                };
            })
            .sort((a, b) => a.entityType.name.localeCompare(b.entityType.name));
    }

    private getUserPlanningVisibility(): { projectIds: Set<string>; rowKeys: Set<string> } {
        const projectIds = new Set<string>();
        const rowKeys = new Set<string>();
        const selectedRoleKeys = this.fcRoleKeys.value ?? [];
        // Role filters should hide projects that are only occupied by filtered-out users.
        const visibleUserIds = !selectedRoleKeys.length
            ? new Set((this.users ?? []).map(user => user.id).filter((id): id is number => id !== undefined))
            : new Set(
                  (this.filteredUniqueRoles ?? []).flatMap(role =>
                      role.users.map(user => user.id).filter((id): id is number => id !== undefined)
                  )
              );

        // Include only planning items that have actual user planning (personeelsplanning)
        this.planningList?.forEach(planning => {
            const projectId = planning.afas_project_id ?? planning.worknumber;
            if (
                projectId &&
                planning.user_planning?.some(up => !up.deleted_at && up.user_id && visibleUserIds.has(up.user_id))
            ) {
                projectIds.add(projectId);
            }
        });

        const planningProjectIdMap = new Map(
            this.planningList?.map(planning => [planning.id, planning.afas_project_id ?? planning.worknumber])
        );

        this.projectDayUserMap.forEach((projectDayUser, key) => {
            const hasVisibleUserPlanning = projectDayUser.userPlanning?.some(
                userPlanning =>
                    !userPlanning.deleted_at && !!userPlanning.user_id && visibleUserIds.has(userPlanning.user_id)
            );
            const hasVisibleDriverPlanning = projectDayUser.planningHas?.some(
                planningHas =>
                    !planningHas.deleted_at &&
                    !!planningHas.driver_user_id &&
                    visibleUserIds.has(planningHas.driver_user_id)
            );

            if (hasVisibleUserPlanning || hasVisibleDriverPlanning) {
                const projectDay = (this.projectDaysList ?? []).find(row => key.endsWith(`-${row.rowKey}`));
                if (projectDay) {
                    rowKeys.add(projectDay.rowKey);
                }
            }

            projectDayUser.userPlanning?.forEach(userPlanning => {
                if (userPlanning.deleted_at || !userPlanning.user_id || !visibleUserIds.has(userPlanning.user_id)) {
                    return;
                }

                const projectId =
                    this.resolveProjectId(userPlanning) ?? planningProjectIdMap.get(userPlanning.planning_id);

                if (projectId) {
                    projectIds.add(projectId);
                }
            });

            projectDayUser.planningHas?.forEach(planningHas => {
                if (
                    planningHas.deleted_at ||
                    !planningHas.driver_user_id ||
                    !visibleUserIds.has(planningHas.driver_user_id)
                ) {
                    return;
                }

                const projectId =
                    this.resolveProjectId(planningHas) ?? planningProjectIdMap.get(planningHas.planning_id);

                if (projectId) {
                    projectIds.add(projectId);
                }
            });
        });

        return { projectIds, rowKeys };
    }

    private refreshPlanningView(): void {
        this.rebuildView();
        this.filterProjectDaysList();
        this.markChangedUserRows();
    }

    /** Marks only the OnPush employee rows whose visible data changed since the previous refresh. */
    private markChangedUserRows(): void {
        const entriesByUser = new Map<string, string[]>();
        // Keys start with yyyy-MM-dd-<userId>
        const add = (key: string, value: unknown) => {
            const userId = key.split('-')[3];
            entriesByUser.set(userId, [...(entriesByUser.get(userId) ?? []), `${key}=${value}`]);
        };
        this.projectDayUserMap.forEach((cell, key) =>
            add(key, [
                cell.type,
                ...[...(cell.userPlanning ?? []), ...(cell.planningHas ?? [])].map(
                    item => `${item.id}:${item.begindate}:${item.enddate}`
                ),
            ])
        );
        this.dayUserMap.forEach((projectId, key) => add(key, projectId));
        this.leaveDayUserMap.forEach((leave, key) => add(key, `${leave.id}:${leave.hourtype}:${leave.comment}`));
        this.blockedMap?.forEach((blocked, key) => add(key, blocked.icon));
        this.userDayConflictMap.forEach((conflict, key) => add(key, conflict));
        this.userDayHasNightMap.forEach((night, key) => add(key, night));
        const rowKeys = this.projectDaysListFiltered?.map(projectDays => projectDays.rowKey).join();

        this.employeePlanningUserRows?.forEach(row => {
            const userId = row.user().id;
            const fingerprint = [
                rowKeys,
                this.userWeekCount.get(userId),
                ...(entriesByUser.get(`${userId}`) ?? []).sort(),
            ].join('|');
            if (this.userRowFingerprints.get(userId) !== fingerprint) {
                this.userRowFingerprints.set(userId, fingerprint);
                row.markForCheck();
            }
        });
    }

    /**
     * Merges rows that ended up on the same project and performer after the save completed.
     */
    private mergeDuplicatePerformerProjectRows(): void {
        const projectIds = [...new Set((this.projectDaysList ?? []).map(projectDay => projectDay.projectId))];

        projectIds.forEach(projectId => {
            const performerIds = [
                ...new Set(
                    (this.projectDaysList ?? [])
                        .filter(projectDay => projectDay.projectId === projectId)
                        .map(projectDay => projectDay.form.controls.performer_id.value)
                        .filter((performerId): performerId is number => performerId != null)
                ),
            ];

            performerIds.forEach(performerId => {
                const matchingRows = (this.projectDaysList ?? []).filter(
                    projectDay =>
                        projectDay.projectId === projectId &&
                        projectDay.form.controls.performer_id.value === performerId
                );

                if (matchingRows.length < 2) {
                    return;
                }

                const keeper = matchingRows[0];
                matchingRows
                    .filter(projectDay => projectDay !== keeper)
                    .forEach(projectDay => this.projectDaysList.splice(this.projectDaysList.indexOf(projectDay), 1));
            });
        });
    }

    /** Register one user_planning mutation in the save queue (and on its team planning). */
    private storeUserPlanningOnProjectDay(userPlanning: UserPlanning, planning?: Planning): void {
        // Re-adding a queued delete restores the saved record instead of creating a new one.
        const queuedDelete = this.userPlanningsToSave.find(
            item =>
                !!item.id &&
                !!item.deleted_at &&
                this.isSameExactUserPlanningMutation(item, userPlanning) &&
                (item.planning_id ?? null) === (userPlanning.planning_id ?? null)
        );
        const existingUserPlanning = queuedDelete ?? this.findActiveExistingUserPlanning(userPlanning);
        const nextUserPlanning = existingUserPlanning ?? userPlanning;

        if (queuedDelete) {
            delete queuedDelete.deleted_at;
            this.userPlanningsToSave.splice(this.userPlanningsToSave.indexOf(queuedDelete), 1);
        } else if (
            !existingUserPlanning &&
            !this.userPlanningsToSave.some(
                item => this.isSameUserPlanningTimeSlot(item, userPlanning) && !item.deleted_at
            )
        ) {
            this.userPlanningsToSave.push(userPlanning);
        }

        this.normalizeUserPlanningsToSave();

        if (planning) {
            if (!planning.user_planning) {
                planning.user_planning = [];
            }

            if (!planning.user_planning.includes(nextUserPlanning)) {
                planning.user_planning.push(nextUserPlanning);
            }
        }
    }

    /**
     * Cross-row conflict guard: does the user already have another planning on this day?
     * A click on the exact same cell is allowed, because that is handled as a toggle/remove.
     */
    private userHasPlanningOnDay(
        day: Date,
        userId: number,
        projectId?: string,
        rowKey?: string,
        candidateStart?: Date,
        candidateEnd?: Date
    ): boolean {
        if (this.projectDayUserMap.has(this.makeKey(day, userId, rowKey ?? projectId))) {
            return false;
        }

        const candidateIsNight = candidateStart && candidateEnd ? Utils.isNight(candidateStart, candidateEnd) : null;
        const conflicts = (item: UserPlanning | PlanningHasEntity, ownerId: number) => {
            if (item.deleted_at || ownerId !== userId) {
                return false;
            }
            const sameDay = !!Utils.matchesDate(item.begindate, day);
            if (
                candidateStart &&
                candidateEnd &&
                Utils.timeRangesOverlap(item.begindate, item.enddate, candidateStart, candidateEnd)
            ) {
                return true;
            }
            if (projectId && sameDay && this.resolveProjectId(item) === projectId && candidateIsNight !== null) {
                return Utils.isNight(item.begindate, item.enddate) === candidateIsNight;
            }
            return !candidateStart || !candidateEnd ? sameDay : false;
        };

        return [...this.projectDayUserMap.values()].some(
            projectDayUser =>
                projectDayUser.userPlanning?.some(up => conflicts(up, up.user_id)) ||
                projectDayUser.planningHas?.some(ph => conflicts(ph, ph.driver_user_id))
        );
    }

    /** Night rule for linked asphalt plannings: any overlap with 00:00-03:00, also when started the evening before. */
    private worksBetweenMidnightAndThree(startDate: Date, endDate: Date): boolean {
        const currentDay = new Date(startDate);
        currentDay.setHours(0, 0, 0, 0);

        while (currentDay < endDate) {
            const nightWindowEnd = new Date(currentDay);
            nightWindowEnd.setHours(3, 0, 0, 0);
            if (startDate < nightWindowEnd && endDate > currentDay) {
                return true;
            }
            currentDay.setDate(currentDay.getDate() + 1);
        }

        return false;
    }

    private getLinkedPlanningForProjectDay(day: Date, projectDay?: ProjectDay): Planning | undefined {
        if (!projectDay?.projectId || !this.projectDayMatchesConfiguredPerformer(projectDay)) {
            return undefined;
        }

        // Only the configured performer row is allowed to mirror asphalt-planning times.
        // Manual extra rows keep their own schedule so parallel plannings can diverge safely.
        const linkedPlanning = this.planningList?.find(
            planning =>
                (planning.afas_project_id ?? planning.worknumber) === projectDay.projectId &&
                Utils.matchesDate(Utils.mainPlanning(planning).begindate, day)
        );
        if (!linkedPlanning) {
            return undefined;
        }

        const { begindate, enddate } = Utils.mainPlanning(linkedPlanning);
        // Only copy asphalt-planning times when the linked planning matches the current day/night mode in the UI.
        return this.worksBetweenMidnightAndThree(new Date(begindate), new Date(enddate)) ===
            !!this.fcNightPlanning.value
            ? linkedPlanning
            : undefined;
    }

    /** Row belongs to the project's (asphalt) performer. */
    projectDayMatchesConfiguredPerformer(projectDay: ProjectDay | undefined): boolean {
        const project = this.projects?.find(p => p.afas_project_id === projectDay?.projectId);
        return [project?.asphalt_performer_id, project?.performer_id]
            .filter(Boolean)
            .includes(projectDay?.form.controls.performer_id.value);
    }

    /** Project id from the item itself, its linked planning or the fetched planning list. */
    private resolveProjectId(
        planningItem?: { afas_project_id?: string; planning?: Planning; planning_id?: number },
        planningList: Planning[] = this.planningList
    ): string | undefined {
        const planning = planningList?.find(p => p.id === planningItem?.planning_id);
        return (
            planningItem?.afas_project_id ??
            planningItem?.planning?.afas_project_id ??
            planningItem?.planning?.worknumber ??
            planning?.afas_project_id ??
            planning?.worknumber
        );
    }

    /**
     * Resolves the exact map entry for the currently addressed visible row/cell.
     * It prefers the row-specific key and only falls back to the plain project key
     * so older/base entries remain reachable during the row-key migration.
     */
    private getProjectDayEntry(
        day: Date,
        entityOrUserId: number,
        projectId?: string,
        rowKey?: string
    ): ProjectDayUserState | undefined {
        // During the transition from project-based keys to row-based keys both variants can exist.
        // Check rowKey first, then fall back to projectId for older/base entries.
        return [...new Set([rowKey, projectId].filter((identifier): identifier is string => !!identifier))]
            .map(identifier => this.projectDayUserMap.get(this.makeKey(day, entityOrUserId, identifier)))
            .find(item => !!item);
    }

    onDateSelected(event: any) {
        const date = new Date(event.value);
        this.goToWeek(Utils.getWeekNumber(date), date.getFullYear());
    }
}

export type ProjectDayUserState = {
    type: 'userPlanning' | 'planning' | 'project';
    userPlanning?: UserPlanning[];
    planningHas?: PlanningHasEntity[];
};

type Team = { entityType: EntityType; entity: Entity; users: User[]; showUsers: boolean };

type ProjectDayPerformerChange = {
    rowKey: string;
    previousPerformerId: number | null;
    nextPerformerId: number | null;
    userPlannings: UserPlanning[];
    planningHass: PlanningHasEntity[];
};

type ProjectDayFormControls = ControlsOf<{
    location: string;
    location_housenumber: string;
    location_place: string;
    location_postcode: string;
    location_straat: string;
    performer_id: number;
    projectmanager_id: number;
}>;

export interface ProjectDay {
    project?: Project;
    projectId: string;
    // Stable key for one visible project row.
    // Multiple rows can exist for the same projectId, so map lookups cannot key only by project anymore.
    rowKey: string;
    /**
     * Last performer value that has already been propagated into the linked user_planning rows.
     * This is used as the sync baseline.
     */
    syncedPerformerId?: number;
    form: FormGroup<ProjectDayFormControls>;
    days: {
        day: Date;
        project?: Project;
        dayLetter: string;
        dayKey: string;
        dayLabel: string;
        cssClass: string;
    }[];
}
